import React, { useState, useEffect, useCallback } from 'react';
import { Routes, Route, Link, useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import DashboardLayout from '../components/common/DashboardLayout';
import StatusBadge from '../components/common/StatusBadge';
import Pagination from '../components/common/Pagination';
import { csrAPI, adoptionAPI } from '../services/api';
import MonitoringMapCard, { PhotoCarousel } from '../components/common/MonitoringMapCard';
import PhotoLightbox from '../components/common/PhotoLightbox';
import ProjectSpeciesSection from '../components/common/ProjectSpeciesSection';
import SpeciesReport from './SpeciesReport';
import { MapContainer, TileLayer, Polygon, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';

/* Helper: fly map to a zone's bounds */
function ZoneFlyTo({ zones, entries, selectedZoneId }) {
  const map = useMap();
  React.useEffect(() => {
    if (!selectedZoneId) return;
    const zone = (zones || []).find(z => z.id === selectedZoneId);
    if (!zone) return;
    const pts = [];
    if (zone.boundary_polygon) zone.boundary_polygon.forEach(p => pts.push([p.lat, p.lng]));
    if (zone.center_latitude && zone.center_longitude) pts.push([zone.center_latitude, zone.center_longitude]);
    (entries || []).filter(e => e.zone_id === selectedZoneId).forEach(e => {
      if (e.latitude && e.longitude) pts.push([e.latitude, e.longitude]);
      (e.photos || []).forEach(p => { if (p.latitude && p.longitude) pts.push([p.latitude, p.longitude]); });
    });
    if (pts.length > 0) {
      const b = L.latLngBounds(pts).pad(0.15);
      map.flyToBounds(b, { duration: 0.8, maxZoom: 18 });
    }
  }, [selectedZoneId, zones, entries, map]);
  return null;
}

const PHOTO_BASE = window.location.origin;
const getPhotoUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `${PHOTO_BASE}${url}`;
};
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Filler
} from 'chart.js';
import { Doughnut } from 'react-chartjs-2';

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Filler
);

const navItems = [
  { path: '', label: 'Dashboard', icon: '\u{1F4CA}' },
  { section: 'Plantation' },
  { path: '/targets', label: 'My Projects', icon: '\u{1F3AF}' },
  { path: '/agencies', label: 'Browse Agencies', icon: '\u{1F3E2}' },
  { section: 'Adoption' },
  { path: '/adopt', label: 'Adopt a Site', icon: '\uD83C\uDF33' },
  { path: '/my-adoptions', label: 'My Adoptions', icon: '\uD83C\uDF3F' },
  { section: 'Monitoring' },
  { path: '/monitoring', label: 'Monitoring Progress', icon: '\u{1F4CA}' },
  { path: '/species-report', label: 'Species Report', icon: '🌻' },
  { section: 'Community' },
  { path: '/ideas', label: 'Collaboration Ideas', icon: '\u{1F4A1}' },
  { path: '/blog', label: 'My Blog Posts', icon: '\u270D\uFE0F' },
  { section: 'Data' },
  { path: '/legacy', label: 'Legacy Data', icon: '\u{1F4C1}' },
];

// ---------------------------------------------------------------------------
// Helper Components
// ---------------------------------------------------------------------------
function ChartCard({ title, subtitle, children }) {
  return (
    <div style={{
      background: '#fff', borderRadius: '14px', padding: '24px',
      boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
    }}>
      <div style={{
        fontSize: '16px', fontWeight: 600, color: '#1a1a2e',
        marginBottom: subtitle ? '4px' : '12px',
        paddingBottom: subtitle ? '4px' : '12px',
        borderBottom: subtitle ? 'none' : '1px solid #f0f0f0',
      }}>
        {title}
      </div>
      {subtitle && (
        <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '12px', paddingBottom: '12px', borderBottom: '1px solid #f0f0f0' }}>
          {subtitle}
        </div>
      )}
      {children}
    </div>
  );
}

function KpiCard({ icon, value, label, sublabel, color, trend }) {
  return (
    <div style={{
      background: '#fff', borderRadius: '14px', padding: '20px',
      display: 'flex', alignItems: 'center', gap: '16px',
      boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
      borderLeft: `4px solid ${color}`,
      transition: 'transform 0.2s, box-shadow 0.2s',
    }}>
      <div style={{
        fontSize: '28px', width: '52px', height: '52px',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        borderRadius: '12px', background: `${color}15`, flexShrink: 0,
      }}>
        {icon}
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: '24px', fontWeight: 700, color: '#1a1a2e', lineHeight: 1.2 }}>
          {typeof value === 'number' ? value.toLocaleString() : value}
        </div>
        <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '2px' }}>{label}</div>
        {sublabel && (
          <div style={{
            fontSize: '11px', marginTop: '2px', fontWeight: 500,
            color: trend === 'up' ? '#16a34a' : trend === 'down' ? '#dc2626' : '#9ca3af',
          }}>
            {sublabel}
          </div>
        )}
      </div>
    </div>
  );
}

function ProgressIndicator({ label, value, max, color, unit = '' }) {
  const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0;
  return (
    <div style={{ marginBottom: '14px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '6px' }}>
        <span style={{ color: '#374151', fontWeight: 500 }}>{label}</span>
        <span style={{ color: '#6b7280' }}>{value.toLocaleString()}{unit} / {max.toLocaleString()}{unit}</span>
      </div>
      <div style={{ background: '#f3f4f6', borderRadius: '8px', height: '10px', overflow: 'hidden' }}>
        <div style={{
          width: `${pct}%`, height: '100%',
          background: `linear-gradient(90deg, ${color}, ${color}cc)`,
          borderRadius: '8px', transition: 'width 1s ease',
        }} />
      </div>
      <div style={{ textAlign: 'right', fontSize: '11px', color: '#9ca3af', marginTop: '3px' }}>{pct.toFixed(1)}%</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Overview (Redesigned with meaningful visualizations)
// ---------------------------------------------------------------------------
function Overview() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    csrAPI.getDashboard()
      .then(res => setStats(res.data))
      .catch(() => toast.error('Failed to load dashboard stats'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="loading-spinner">Loading...</div>;

  const today = new Date().toLocaleDateString('en-US', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
  });

  // Extract data from API response
  const totalTargets = stats?.total_targets ?? 0;
  const totalPlants = stats?.total_plants ?? 0;
  const totalApplications = stats?.total_applications ?? 0;
  const pendingApplications = stats?.pending_applications ?? 0;
  const acceptedApplications = stats?.accepted_applications ?? 0;
  const totalAllocations = stats?.total_allocations ?? 0;
  const reportsCount = stats?.reports_count ?? 0;
  const reportStatus = stats?.report_status ?? {};
  const agenciesEngaged = stats?.agencies_engaged ?? 0;
  const targetProgress = stats?.target_progress ?? [];

  const totalTreesMonitored = stats?.total_trees_monitored ?? 0;

  // Status color/label map
  const statusMap = {
    draft: { label: 'Draft', color: '#9e9e9e', bg: '#f5f5f5' },
    pending_review: { label: 'Pending', color: '#ff9800', bg: '#fff3e0' },
    published: { label: 'Published', color: '#2196f3', bg: '#e3f2fd' },
    in_progress: { label: 'In Progress', color: '#4caf50', bg: '#e8f5e9' },
    completed: { label: 'Completed', color: '#1a7431', bg: '#e8f5e9' },
    cancelled: { label: 'Cancelled', color: '#ef5350', bg: '#ffebee' },
  };

  return (
    <div>
      {/* Welcome Banner */}
      <div style={{
        background: 'linear-gradient(135deg, #1a7431 0%, #2e7d32 50%, #43a047 100%)',
        color: '#fff', padding: '28px 32px', borderRadius: '16px', marginBottom: '24px',
        position: 'relative', overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', top: '-40px', right: '-20px', fontSize: '140px', opacity: 0.08, lineHeight: 1 }}>{'\uD83C\uDF33'}</div>
        <h2 style={{ margin: '0 0 6px 0', fontSize: '26px', fontWeight: 700 }}>CSR Dashboard</h2>
        <p style={{ margin: 0, opacity: 0.9, fontSize: '14px', maxWidth: '600px' }}>
          Track your plantation projects, manage agency partnerships, and measure environmental impact.
        </p>
        <p style={{ margin: '10px 0 0 0', opacity: 0.7, fontSize: '12px' }}>{today}</p>
      </div>

      {/* ===== COMPACT KPI STRIP ===== */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '14px', marginBottom: '24px',
      }}>
        {[
          { icon: '\uD83C\uDF31', value: totalTreesMonitored.toLocaleString(), label: 'Trees Monitored', sub: `of ${totalPlants.toLocaleString()} target`, color: '#1a7431' },
          { icon: '\uD83D\uDCCA', value: `${totalPlants > 0 ? ((totalTreesMonitored / totalPlants) * 100).toFixed(1) : 0}%`, label: 'Completion', sub: totalPlants > 0 ? `${Math.max(0, totalPlants - totalTreesMonitored).toLocaleString()} remaining` : 'No target set', color: totalPlants > 0 && (totalTreesMonitored / totalPlants) >= 0.5 ? '#2e7d32' : '#f57c00' },
          { icon: '\uD83C\uDFE2', value: agenciesEngaged, label: 'Agencies', sub: `${totalAllocations} allocations`, color: '#1976d2' },
          { icon: '\uD83D\uDCCB', value: reportsCount, label: 'Reports', sub: `${reportStatus.approved || 0} approved`, color: '#e65100' },
        ].map(k => (
          <div key={k.label} style={{
            background: '#fff', borderRadius: '12px', padding: '16px', textAlign: 'center',
            boxShadow: '0 1px 8px rgba(0,0,0,0.06)', borderTop: `3px solid ${k.color}`,
          }}>
            <div style={{ fontSize: '24px', marginBottom: '4px' }}>{k.icon}</div>
            <div style={{ fontSize: '22px', fontWeight: 700, color: k.color }}>{k.value}</div>
            <div style={{ fontSize: '12px', fontWeight: 600, color: '#374151', marginTop: '2px' }}>{k.label}</div>
            <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '2px' }}>{k.sub}</div>
          </div>
        ))}
      </div>

      {/* Monitoring & GPS Map (+ Monitoring History) */}
      <MonitoringMapCard fetchSummary={csrAPI.getMonitoringSummary} />

      {/* ===== PER-TARGET PROGRESS CARDS ===== */}
      {targetProgress.length > 0 && (
        <div style={{
          background: '#fff', borderRadius: '14px', padding: '24px',
          boxShadow: '0 2px 12px rgba(0,0,0,0.06)', marginBottom: '24px',
        }}>
          <div style={{ fontSize: '16px', fontWeight: 600, color: '#1a1a2e', marginBottom: '18px', paddingBottom: '12px', borderBottom: '1px solid #f0f0f0' }}>
            Project-wise Progress
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: targetProgress.length === 1 ? '1fr' : 'repeat(auto-fill, minmax(320px, 1fr))', gap: '16px' }}>
            {targetProgress.map((tp, idx) => {
              const tpCount = tp.monitored || tp.planted || 0;
              const plantPct = tp.total_plants > 0 ? Math.min((tpCount / tp.total_plants) * 100, 100) : 0;
              const allocPct = tp.total_plants > 0 ? Math.min((tp.allocated_plants / tp.total_plants) * 100, 100) : 0;
              const st = statusMap[tp.status] || { label: tp.status, color: '#666', bg: '#f5f5f5' };
              return (
                <div key={idx} style={{
                  border: '1px solid #e5e7eb', borderRadius: '12px', padding: '18px',
                  position: 'relative', overflow: 'hidden',
                }}>
                  {/* Header */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '14px' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '14px', fontWeight: 600, color: '#1a1a2e', marginBottom: '4px' }}>{tp.name}</div>
                      <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '6px', background: st.bg, color: st.color, fontWeight: 600 }}>{st.label}</span>
                    </div>
                    {/* Circular progress */}
                    <svg width="52" height="52" viewBox="0 0 52 52" style={{ flexShrink: 0 }}>
                      <circle cx="26" cy="26" r="22" fill="none" stroke="#f0f0f0" strokeWidth="4" />
                      <circle cx="26" cy="26" r="22" fill="none" stroke={plantPct >= 75 ? '#1a7431' : plantPct >= 40 ? '#ff9800' : '#ef5350'}
                        strokeWidth="4" strokeDasharray={`${plantPct * 1.382} 200`}
                        strokeLinecap="round" transform="rotate(-90 26 26)" />
                      <text x="26" y="26" textAnchor="middle" dominantBaseline="central"
                        style={{ fontSize: '11px', fontWeight: 700, fill: '#374151' }}>{plantPct.toFixed(0)}%</text>
                    </svg>
                  </div>
                  {/* Stats row */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px', marginBottom: '14px' }}>
                    <div style={{ textAlign: 'center', padding: '8px', background: '#f9fafb', borderRadius: '8px' }}>
                      <div style={{ fontSize: '15px', fontWeight: 700, color: '#1a7431' }}>{tpCount.toLocaleString()}</div>
                      <div style={{ fontSize: '10px', color: '#6b7280' }}>Monitored</div>
                    </div>
                    <div style={{ textAlign: 'center', padding: '8px', background: '#f9fafb', borderRadius: '8px' }}>
                      <div style={{ fontSize: '15px', fontWeight: 700, color: '#1976d2' }}>{tp.agencies}</div>
                      <div style={{ fontSize: '10px', color: '#6b7280' }}>Agencies</div>
                    </div>
                    <div style={{ textAlign: 'center', padding: '8px', background: '#f9fafb', borderRadius: '8px' }}>
                      <div style={{ fontSize: '15px', fontWeight: 700, color: '#e65100' }}>{tp.approved_reports}/{tp.reports}</div>
                      <div style={{ fontSize: '10px', color: '#6b7280' }}>Reports</div>
                    </div>
                  </div>
                  {/* Plantation bar */}
                  <div style={{ marginBottom: '8px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', marginBottom: '4px' }}>
                      <span style={{ color: '#374151', fontWeight: 500 }}>Plantation</span>
                      <span style={{ color: '#6b7280' }}>{tpCount.toLocaleString()} / {tp.total_plants.toLocaleString()}</span>
                    </div>
                    <div style={{ background: '#f3f4f6', borderRadius: '6px', height: '8px', overflow: 'hidden', position: 'relative' }}>
                      <div style={{ position: 'absolute', width: `${allocPct}%`, height: '100%', background: '#c8e6c9', borderRadius: '6px' }} />
                      <div style={{ position: 'absolute', width: `${plantPct}%`, height: '100%', background: '#1a7431', borderRadius: '6px', transition: 'width 0.8s' }} />
                    </div>
                    <div style={{ display: 'flex', gap: '12px', marginTop: '3px', fontSize: '10px', color: '#9ca3af' }}>
                      <span><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: '#1a7431', marginRight: 3 }}></span>Monitored</span>
                      <span><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: '#c8e6c9', marginRight: 3 }}></span>Allocated</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ===== ALLOCATION PIPELINE ===== */}
      <div style={{ marginBottom: '24px' }}>
        {/* Application & Allocation Pipeline */}
        <div style={{ background: '#fff', borderRadius: '14px', padding: '24px', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
          <div style={{ fontSize: '16px', fontWeight: 600, color: '#1a1a2e', marginBottom: '4px' }}>Agency Pipeline</div>
          <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '16px', paddingBottom: '12px', borderBottom: '1px solid #f0f0f0' }}>
            Application to allocation workflow
          </div>
          {/* Pipeline stages */}
          {[
            { label: 'Applications Received', value: totalApplications, icon: '\uD83D\uDCE8', color: '#1976d2', bg: '#e3f2fd' },
            { label: 'Pending Review', value: pendingApplications, icon: '\u23F3', color: '#ff9800', bg: '#fff3e0' },
            { label: 'Accepted', value: acceptedApplications, icon: '\u2705', color: '#4caf50', bg: '#e8f5e9' },
            { label: 'Work Allocated', value: totalAllocations, icon: '\uD83D\uDD17', color: '#7c4dff', bg: '#ede7f6' },
            { label: 'Reports Submitted', value: reportsCount, icon: '\uD83D\uDCDD', color: '#e65100', bg: '#fff3e0' },
            { label: 'Reports Approved', value: reportStatus.approved || 0, icon: '\uD83C\uDF1F', color: '#1a7431', bg: '#e8f5e9' },
          ].map((stage, i) => (
            <div key={stage.label} style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '10px', position: 'relative' }}>
              {/* Connector line */}
              {i < 5 && <div style={{ position: 'absolute', left: '17px', top: '36px', width: '2px', height: '14px', background: '#e5e7eb' }} />}
              <div style={{
                width: '36px', height: '36px', borderRadius: '10px', background: stage.bg,
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px', flexShrink: 0,
              }}>{stage.icon}</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '12px', color: '#6b7280' }}>{stage.label}</div>
                <div style={{ fontSize: '18px', fontWeight: 700, color: stage.color }}>{stage.value}</div>
              </div>
              {/* Mini bar */}
              {totalApplications > 0 && i > 0 && (
                <div style={{ width: '60px', height: '6px', background: '#f3f4f6', borderRadius: '3px', overflow: 'hidden' }}>
                  <div style={{ width: `${Math.min((stage.value / totalApplications) * 100, 100)}%`, height: '100%', background: stage.color, borderRadius: '3px' }} />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* ===== OVERALL PROGRESS BARS ===== */}
      <div style={{
        background: '#fff', borderRadius: '14px', padding: '24px',
        boxShadow: '0 2px 12px rgba(0,0,0,0.06)', marginBottom: '24px',
      }}>
        <div style={{ fontSize: '16px', fontWeight: 600, color: '#1a1a2e', marginBottom: '16px', paddingBottom: '12px', borderBottom: '1px solid #f0f0f0' }}>
          Overall Progress
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
          <div>
            <ProgressIndicator label="Trees Monitored vs Target" value={totalTreesMonitored} max={totalPlants || 1} color="#1a7431" />
            <ProgressIndicator label="Applications Accepted" value={acceptedApplications} max={totalApplications || 1} color="#ff8f00" />
          </div>
          <div>
            <ProgressIndicator label="Reports Approved" value={reportStatus.approved || 0} max={reportsCount || 1} color="#2e7d32" />
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div style={{
        background: '#fff', borderRadius: '14px', padding: '24px',
        boxShadow: '0 2px 12px rgba(0,0,0,0.06)', marginBottom: '28px',
      }}>
        <div style={{ fontSize: '16px', fontWeight: 600, color: '#1a1a2e', marginBottom: '18px', paddingBottom: '12px', borderBottom: '1px solid #f0f0f0' }}>
          Quick Actions
        </div>
        <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap' }}>
          {[
            { to: '/csr/targets', icon: '\uD83C\uDFAF', label: `Manage Projects (${totalTargets})`, bg: 'linear-gradient(135deg, #1a7431, #2e7d32)' },
            { to: '/csr/agencies', icon: '\uD83C\uDFE2', label: `Browse Agencies (${agenciesEngaged})`, bg: 'linear-gradient(135deg, #1565c0, #1976d2)' },
            { to: '/csr/monitoring', icon: '\uD83D\uDCCA', label: 'Monitoring Progress', bg: 'linear-gradient(135deg, #e65100, #ff8f00)' },
            { to: '/csr/adopt', icon: '\uD83C\uDF33', label: 'Adopt a Site', bg: 'linear-gradient(135deg, #6a1b9a, #7c4dff)' },
          ].map(action => (
            <Link key={action.to} to={action.to} style={{
              display: 'inline-flex', alignItems: 'center', gap: '8px',
              padding: '12px 22px', borderRadius: '10px', background: action.bg,
              color: '#fff', textDecoration: 'none', fontSize: '14px', fontWeight: 500,
              transition: 'transform 0.2s, box-shadow 0.2s',
              boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
            }}>
              {action.icon} {action.label}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// New Target Modal
// ---------------------------------------------------------------------------
function NewTargetModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    title: '',
    description: '',
    total_plants: '',
    plantation_type: '',
    species_details: '',
    location_description: '',
    city: '',
    state: '',
    latitude: '',
    longitude: '',
    start_date: '',
    end_date: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.total_plants) {
      toast.error('Title and total plants are required');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        total_plants: Number(form.total_plants),
        latitude: form.latitude ? Number(form.latitude) : undefined,
        longitude: form.longitude ? Number(form.longitude) : undefined,
      };
      const res = await csrAPI.createTarget(payload);
      toast.success('Target created successfully');
      onCreated(res.data.target || res.data);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create target');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 640, maxHeight: '90vh', overflow: 'auto' }}>
        <div className="modal-header">
          <h3 style={{ margin: 0 }}>New Plantation Project</h3>
          <button className="btn btn-secondary btn-sm" onClick={onClose}>&times;</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Title *</label>
            <input name="title" className="form-control" value={form.title} onChange={handleChange} required />
          </div>
          <div className="form-group">
            <label>Description</label>
            <textarea name="description" className="form-control" rows={3} value={form.description} onChange={handleChange} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="form-group">
              <label>Total Plants *</label>
              <input name="total_plants" type="number" min="1" className="form-control" value={form.total_plants} onChange={handleChange} required />
            </div>
            <div className="form-group">
              <label>Plantation Type</label>
              <select name="plantation_type" className="form-control" value={form.plantation_type} onChange={handleChange}>
                <option value="">Select type</option>
                <option value="avenue">Avenue</option>
                <option value="boundary">Boundary</option>
                <option value="block">Block</option>
                <option value="mixed">Mixed</option>
                <option value="forest">Forest</option>
              </select>
            </div>
          </div>
          <div className="form-group">
            <label>Species Details</label>
            <textarea name="species_details" className="form-control" rows={2} value={form.species_details} onChange={handleChange} placeholder="e.g. Neem, Peepal, Banyan" />
          </div>
          <div className="form-group">
            <label>Location Description</label>
            <input name="location_description" className="form-control" value={form.location_description} onChange={handleChange} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="form-group">
              <label>City</label>
              <input name="city" className="form-control" value={form.city} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>State</label>
              <input name="state" className="form-control" value={form.state} onChange={handleChange} />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="form-group">
              <label>Latitude</label>
              <input name="latitude" type="number" step="any" className="form-control" value={form.latitude} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>Longitude</label>
              <input name="longitude" type="number" step="any" className="form-control" value={form.longitude} onChange={handleChange} />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="form-group">
              <label>Start Date</label>
              <input name="start_date" type="date" className="form-control" value={form.start_date} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>End Date</label>
              <input name="end_date" type="date" className="form-control" value={form.end_date} onChange={handleChange} />
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? 'Creating...' : 'Create Project'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Targets List
// ---------------------------------------------------------------------------
function TargetsList() {
  const [targets, setTargets] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);

  const fetchTargets = useCallback((p) => {
    setLoading(true);
    csrAPI.getTargets({ page: p, per_page: 10 })
      .then(res => {
        setTargets(res.data.targets || res.data.items || []);
        setPages(res.data.pages || 1);
      })
      .catch(() => toast.error('Failed to load projects'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchTargets(page); }, [page, fetchTargets]);

  const handleCreated = () => {
    setShowModal(false);
    setPage(1);
    fetchTargets(1);
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h3 style={{ margin: 0 }}>My Plantation Projects</h3>
        <button className="btn btn-primary" onClick={() => setShowModal(true)}>+ New Project</button>
      </div>

      <Link to="/csr/monitoring" style={{
        display: 'flex', alignItems: 'center', gap: 10,
        background: 'linear-gradient(135deg, #1565c0, #1976d2)', color: '#fff',
        padding: '12px 18px', borderRadius: 10, marginBottom: 16, textDecoration: 'none', fontSize: 14, fontWeight: 500,
      }}>
        <span style={{ fontSize: 20 }}>📊</span>
        View Monitoring Progress for All Projects
        <span style={{ marginLeft: 'auto', opacity: 0.7 }}>→</span>
      </Link>

      {loading ? (
        <div className="empty-state">Loading projects...</div>
      ) : targets.length === 0 ? (
        <div className="empty-state">No projects found. Click "New Project" to create one.</div>
      ) : (
        <>
          <div className="card">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Total Plants</th>
                  <th>Status</th>
                  <th>Date</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {targets.map(t => (
                  <tr key={t.id}>
                    <td>{t.title}</td>
                    <td>{(t.total_plants || 0).toLocaleString()}</td>
                    <td><StatusBadge status={t.status} /></td>
                    <td>{t.created_at ? new Date(t.created_at).toLocaleDateString() : '-'}</td>
                    <td><Link to={`/csr/targets/${t.id}`} className="btn btn-secondary btn-sm">View</Link></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination page={page} pages={pages} onPageChange={setPage} />
        </>
      )}

      {showModal && <NewTargetModal onClose={() => setShowModal(false)} onCreated={handleCreated} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Target Detail
// ---------------------------------------------------------------------------
function TargetDetail() {
  const HISTORY_PER_PAGE = 5;
  const { id } = useParams();
  const navigate = useNavigate();
  const [target, setTarget] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [selectedZoneId, setSelectedZoneId] = useState(null);
  const [historyPage, setHistoryPage] = useState(1);
  const [lightboxPhotos, setLightboxPhotos] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [lightboxEntry, setLightboxEntry] = useState('');

  const fetchTarget = useCallback(() => {
    setLoading(true);
    csrAPI.getTarget(id)
      .then(res => setTarget(res.data.target || res.data))
      .catch(() => {
        toast.error('Failed to load project details');
        navigate('/csr/targets');
      })
      .finally(() => setLoading(false));
  }, [id, navigate]);

  useEffect(() => { fetchTarget(); }, [fetchTarget]);

  const handleApplication = async (appId, action) => {
    setActionLoading(appId);
    try {
      if (action === 'accept') {
        await csrAPI.acceptApplication(id, appId);
        toast.success('Application accepted');
      } else {
        await csrAPI.rejectApplication(id, appId);
        toast.success('Application rejected');
      }
      fetchTarget();
    } catch (err) {
      toast.error(err.response?.data?.error || `Failed to ${action} application`);
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) return <div className="empty-state">Loading project details...</div>;
  if (!target) return <div className="empty-state">Project not found.</div>;

  const applications = target.applications || [];
  const milestones = target.milestones || [];
  const allocations = target.allocations || [];

  return (
    <div>
      <button className="btn btn-secondary btn-sm" onClick={() => navigate('/csr/targets')} style={{ marginBottom: 16 }}>
        &larr; Back to Projects
      </button>

      <div className="card" style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h2 style={{ margin: '0 0 8px' }}>{target.title}</h2>
            <StatusBadge status={target.status} />
          </div>
          <div style={{ textAlign: 'right', fontSize: 14, color: '#666' }}>
            <div>Created: {target.created_at ? new Date(target.created_at).toLocaleDateString() : '-'}</div>
            {target.start_date && <div>Start: {new Date(target.start_date).toLocaleDateString()}</div>}
            {target.end_date && <div>End: {new Date(target.end_date).toLocaleDateString()}</div>}
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 24 }}>
        <div className="metric-card">
          <div className="value">{(target.total_plants || 0).toLocaleString()}</div>
          <div className="label">Total Plants</div>
        </div>
        <div className="metric-card">
          <div className="value" style={{ fontSize: 'clamp(16px, 2.5vw, 28px)', wordBreak: 'break-word', lineHeight: 1.2 }}>{target.plantation_type || '-'}</div>
          <div className="label">Plantation Type</div>
        </div>
        <div className="metric-card">
          <div className="value" style={{ fontSize: 'clamp(18px, 3vw, 32px)', wordBreak: 'break-word', lineHeight: 1.2 }}>{target.city && target.state ? `${target.city}, ${target.state}` : target.city || target.state || '-'}</div>
          <div className="label">Location</div>
        </div>
      </div>

      {target.description && (
        <div className="card" style={{ marginBottom: 24 }}>
          <h4 style={{ marginTop: 0 }}>Description</h4>
          <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{target.description}</p>
        </div>
      )}

      {target.species_details && (
        <div className="card" style={{ marginBottom: 24 }}>
          <h4 style={{ marginTop: 0 }}>Species Details</h4>
          <p style={{ margin: 0 }}>{target.species_details}</p>
        </div>
      )}

      {target.location_description && (
        <div className="card" style={{ marginBottom: 24 }}>
          <h4 style={{ marginTop: 0 }}>Location Details</h4>
          <p style={{ margin: 0 }}>{target.location_description}</p>
          {target.latitude && target.longitude && (
            <p style={{ margin: '8px 0 0', fontSize: 13, color: '#888' }}>
              Coordinates: {target.latitude}, {target.longitude}
            </p>
          )}
        </div>
      )}

      {/* Plant Species (read-only for CSR) */}
      <ProjectSpeciesSection targetId={target.id} adminMode={false} />

      {/* GPS Map — zones and photo markers */}
      {(() => {
        const zones = target.monitoring_zones || [];
        const entries = target.monitoring_entries || [];
        const zoneColors = ['#1b5e20', '#0d47a1', '#e65100', '#6a1b9a', '#c62828', '#00838f', '#ad1457', '#4e342e'];
        const zoneColorMap = {};
        zones.forEach((z, i) => { zoneColorMap[z.id] = zoneColors[i % zoneColors.length]; });
        const allPoints = [];
        zones.forEach(z => {
          if (z.boundary_polygon) z.boundary_polygon.forEach(p => allPoints.push([p.lat, p.lng]));
          if (z.center_latitude && z.center_longitude) allPoints.push([z.center_latitude, z.center_longitude]);
        });
        entries.forEach(e => {
          if (e.latitude && e.longitude) allPoints.push([e.latitude, e.longitude]);
          (e.photos || []).forEach(p => { if (p.latitude && p.longitude) allPoints.push([p.latitude, p.longitude]); });
        });
        if (allPoints.length === 0) return null;
        const bounds = L.latLngBounds(allPoints);
        return (
          <div className="card" style={{ marginBottom: 24 }}>
            <h4 style={{ marginTop: 0 }}>Monitoring & GPS Mapping</h4>
            <div style={{ height: 450, borderRadius: 12, overflow: 'hidden', border: '1px solid #e0e0e0' }}>
              <MapContainer bounds={bounds.pad(0.1)} style={{ height: '100%', width: '100%' }} scrollWheelZoom={true}>
                <TileLayer url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}" attribution="Esri" />
                <ZoneFlyTo zones={zones} entries={entries} selectedZoneId={selectedZoneId} />
                {zones.map((z) => z.boundary_polygon && (
                  <Polygon key={z.id} positions={z.boundary_polygon.map(p => [p.lat, p.lng])}
                    pathOptions={{ color: zoneColorMap[z.id], weight: selectedZoneId === z.id ? 5 : 3, fillOpacity: selectedZoneId === z.id ? 0.35 : 0.2 }}>
                    <Popup><strong>{z.name}</strong><br />{z.tree_count?.toLocaleString()} trees{z.area_sqm ? ` | ${Number(z.area_sqm).toLocaleString()} sq m` : ''}</Popup>
                  </Polygon>
                ))}
                {(() => {
                  // Group geotagged photos by zone (one dot per zone)
                  const groupMap = new Map();
                  entries.forEach(e => {
                    (e.photos || []).forEach(p => {
                      if (!(p.latitude && p.longitude)) return;
                      const key = e.zone_id ? `zone-${e.zone_id}` : `entry-${e.id}`;
                      if (!groupMap.has(key)) {
                        groupMap.set(key, {
                          zoneId: e.zone_id || null,
                          label: e.zone_name || 'Monitoring Entry',
                          color: zoneColorMap[e.zone_id] || '#e65100',
                          photos: [],
                        });
                      }
                      groupMap.get(key).photos.push({
                        id: p.id,
                        url: getPhotoUrl(p.photo_url),
                        type: p.photo_type,
                        lat: Number(p.latitude),
                        lng: Number(p.longitude),
                        zoneName: e.zone_name,
                        entryDate: e.monitoring_date,
                      });
                    });
                  });
                  const groups = [];
                  groupMap.forEach((g, key) => {
                    if (g.photos.length === 0) return;
                    const cLat = g.photos.reduce((s, p) => s + p.lat, 0) / g.photos.length;
                    const cLng = g.photos.reduce((s, p) => s + p.lng, 0) / g.photos.length;
                    groups.push({ key, centroidLat: cLat, centroidLng: cLng, ...g });
                  });
                  return groups.map(g => {
                    const isSelected = selectedZoneId && g.zoneId === selectedZoneId;
                    const size = isSelected ? 34 : 26;
                    const count = g.photos.length;
                    return (
                      <Marker key={g.key} position={[g.centroidLat, g.centroidLng]}
                        icon={L.divIcon({
                          className: '',
                          html: `<div style="background:${g.color};color:#fff;border-radius:50%;width:${size}px;height:${size}px;display:flex;align-items:center;justify-content:center;border:3px solid #fff;box-shadow:0 0 6px rgba(0,0,0,0.5);font-size:${size >= 30 ? 13 : 11}px;font-weight:700;${isSelected ? 'animation:pulse 1s infinite;' : ''}">${count}</div>`,
                          iconSize: [size, size],
                          iconAnchor: [size/2, size/2],
                        })}>
                        <Popup maxWidth={320} minWidth={280}>
                          <PhotoCarousel
                            photos={g.photos}
                            label={g.label}
                            centroidLat={g.centroidLat}
                            centroidLng={g.centroidLng}
                          />
                        </Popup>
                      </Marker>
                    );
                  });
                })()}
              </MapContainer>
            </div>
            {/* Clickable zone legend */}
            <div style={{ display: 'flex', gap: 12, marginTop: 12, flexWrap: 'wrap', fontSize: 12 }}>
              <span onClick={() => setSelectedZoneId(null)}
                style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', padding: '4px 10px', borderRadius: 8, background: !selectedZoneId ? '#e8f5e9' : '#fff', border: !selectedZoneId ? '2px solid #4caf50' : '1px solid #e0e0e0', fontWeight: !selectedZoneId ? 700 : 400, transition: 'all 0.2s' }}>
                🗺️ All Zones
              </span>
              {zones.map((z) => (
                <span key={z.id} onClick={() => setSelectedZoneId(selectedZoneId === z.id ? null : z.id)}
                  style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', padding: '4px 10px', borderRadius: 8, background: selectedZoneId === z.id ? zoneColorMap[z.id] + '22' : '#fff', border: selectedZoneId === z.id ? `2px solid ${zoneColorMap[z.id]}` : '1px solid #e0e0e0', fontWeight: selectedZoneId === z.id ? 700 : 400, transition: 'all 0.2s' }}>
                  <span style={{ width: 12, height: 12, borderRadius: 3, background: zoneColorMap[z.id], display: 'inline-block' }} />
                  {z.name} ({(z.tree_count || 0).toLocaleString()} trees)
                </span>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Monitoring Zone Cards */}
      {target.monitoring_zones && target.monitoring_zones.length > 0 && (() => {
        const zoneColors = ['#1b5e20', '#0d47a1', '#e65100', '#6a1b9a', '#c62828', '#00838f', '#ad1457', '#4e342e'];
        return (
        <div className="card" style={{ marginBottom: 24 }}>
          <h4 style={{ marginTop: 0 }}>Monitoring Zones <span style={{ fontSize: 13, fontWeight: 400, color: '#888' }}>— click a zone to see it on the map</span></h4>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
            {target.monitoring_zones.map((z, i) => {
              const isActive = selectedZoneId === z.id;
              const zColor = zoneColors[i % zoneColors.length];
              return (
              <div key={z.id} onClick={() => { setSelectedZoneId(isActive ? null : z.id); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                style={{ border: isActive ? `2px solid ${zColor}` : '1px solid #e0e0e0', borderRadius: 12, padding: 16, background: isActive ? zColor + '0a' : '#fafafa', cursor: 'pointer', transition: 'all 0.25s', transform: isActive ? 'scale(1.02)' : 'scale(1)', boxShadow: isActive ? `0 4px 16px ${zColor}33` : 'none' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <span style={{ width: 14, height: 14, borderRadius: 4, background: zColor, display: 'inline-block', flexShrink: 0 }} />
                  <span style={{ fontWeight: 700, color: '#1b5e20', fontSize: 15 }}>{z.name}</span>
                  {isActive && <span style={{ marginLeft: 'auto', fontSize: 11, background: zColor, color: '#fff', padding: '2px 8px', borderRadius: 10 }}>Viewing on map</span>}
                </div>
                {z.description && <div style={{ fontSize: 13, color: '#666', marginBottom: 8 }}>{z.description}</div>}
                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 13 }}>
                  <span>Trees: <strong>{(z.tree_count || 0).toLocaleString()}</strong></span>
                  {z.area_sqm && <span>Area: <strong>{Number(z.area_sqm).toLocaleString()}</strong> sq m</span>}
                  <span style={{ textTransform: 'capitalize', color: '#888' }}>{z.zone_type?.replace('_', ' ')}</span>
                </div>
                {z.boundary_polygon ? (
                  <div style={{ marginTop: 8, fontSize: 12, color: '#4caf50', fontWeight: 600 }}>📍 GPS Boundary Mapped ({z.boundary_polygon.length} points)</div>
                ) : (
                  <div style={{ marginTop: 8, fontSize: 12, color: '#ff9800' }}>GPS boundary not mapped yet</div>
                )}
                <div style={{ marginTop: 6, fontSize: 12, color: '#999' }}>{z.entries_count || 0} monitoring {(z.entries_count || 0) === 1 ? 'entry' : 'entries'}</div>
              </div>
            );})}
          </div>
        </div>
      );})()}

      {/* Monitoring Entries with Photos */}
      {target.monitoring_entries && target.monitoring_entries.length > 0 && (() => {
        const allEntries = target.monitoring_entries;
        const historyPages = Math.max(1, Math.ceil(allEntries.length / HISTORY_PER_PAGE));
        const currentPage = Math.min(historyPage, historyPages);
        const pagedEntries = allEntries.slice((currentPage - 1) * HISTORY_PER_PAGE, currentPage * HISTORY_PER_PAGE);
        return (
        <div className="card" style={{ marginBottom: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
            <h4 style={{ margin: 0 }}>Monitoring History</h4>
            <span style={{ fontSize: 12, color: '#888' }}>
              Showing {(currentPage - 1) * HISTORY_PER_PAGE + 1}&ndash;{Math.min(currentPage * HISTORY_PER_PAGE, allEntries.length)} of {allEntries.length}
            </span>
          </div>
          {pagedEntries.map(e => {
            const healthColors = { excellent: '#1b5e20', good: '#4caf50', fair: '#ff9800', poor: '#f44336', critical: '#b71c1c' };
            const photos = e.photos || [];
            return (
              <div key={e.id} style={{ border: '1px solid #e8e8e8', borderRadius: 12, padding: 16, marginBottom: 12, background: '#fafffe' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                  <div>
                    <strong>{e.zone_name || 'Unknown Zone'}</strong>
                    <span style={{ fontSize: 12, color: '#888', marginLeft: 8 }}>{e.monitoring_date ? new Date(e.monitoring_date).toLocaleDateString() : ''}</span>
                    {e.agency_name && <span style={{ fontSize: 12, color: '#666', marginLeft: 8 }}>by {e.agency_name}</span>}
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <span style={{ padding: '3px 10px', borderRadius: 10, fontSize: 12, fontWeight: 600, color: healthColors[e.overall_health] || '#666', background: (healthColors[e.overall_health] || '#666') + '18', textTransform: 'capitalize' }}>{e.overall_health}</span>
                    <StatusBadge status={e.status} />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 20, marginTop: 10, fontSize: 13, flexWrap: 'wrap' }}>
                  <span>Trees: <strong>{(e.total_trees_monitored || 0).toLocaleString()}</strong></span>
                  <span>Healthy: <strong style={{ color: '#4caf50' }}>{e.trees_healthy || 0}</strong></span>
                  {e.survival_rate != null && <span>Survival: <strong>{e.survival_rate}%</strong></span>}
                </div>
                {e.latitude && e.longitude && (
                  <div style={{ marginTop: 8, fontSize: 11, color: '#1976d2' }}>
                    📍 Entry GPS: {Number(e.latitude).toFixed(6)}, {Number(e.longitude).toFixed(6)}
                  </div>
                )}
                {photos.length > 0 && (
                  <div style={{ marginTop: 12 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#666', marginBottom: 6 }}>Photos ({photos.length})</div>
                    <div style={{ display: 'flex', gap: 10, overflowX: 'auto', paddingBottom: 4 }}>
                      {photos.map((p, pIdx) => (
                        <div key={p.id} onClick={() => { setLightboxPhotos(photos); setLightboxIndex(pIdx); setLightboxEntry(e.zone_name || ''); }}
                          style={{ flexShrink: 0, textAlign: 'center', cursor: 'pointer' }}>
                          <div style={{ position: 'relative', width: 100, height: 75 }}>
                            <img src={getPhotoUrl(p.photo_url)} alt={p.photo_type}
                              style={{ width: 100, height: 75, objectFit: 'cover', borderRadius: 8, border: '2px solid #e0e0e0' }}
                              onError={ev => { ev.target.style.display = 'none'; }} />
                            {p.latitude && p.longitude && (
                              <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'rgba(0,0,0,0.65)', color: '#fff', fontSize: 8, padding: '2px 3px', borderRadius: '0 0 6px 6px', lineHeight: 1.2, textAlign: 'center' }}>
                                {Number(p.latitude).toFixed(5)}, {Number(p.longitude).toFixed(5)}
                              </div>
                            )}
                          </div>
                          <div style={{ fontSize: 10, color: '#888', marginTop: 2, textTransform: 'capitalize' }}>{p.photo_type}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          {historyPages > 1 && (
            <div style={{ marginTop: 12, marginBottom: 8 }}>
              <Pagination page={currentPage} pages={historyPages} onPageChange={setHistoryPage} />
            </div>
          )}
          {/* Summary stats (planted count from project) */}
          {(() => {
            const entries = target.monitoring_entries;
            const totalMonitored = target.plants_reported || 0;
            const _approved = entries.filter(e => e.status === 'approved');
            const _treesSum = _approved.reduce((s, e) => s + (e.total_trees_monitored || 0), 0);
            const _wt = _approved.reduce((s, e) => s + (e.total_trees_monitored || 0) * (e.survival_rate || 0), 0);
            const avgSurvival = _treesSum > 0 ? (_wt / _treesSum).toFixed(1) : null;
            const totalPhotos = entries.reduce((s, e) => s + (e.photos?.length || 0), 0);
            return (
              <div style={{ display: 'flex', gap: 24, marginTop: 8, padding: '12px 16px', background: '#f5f5f5', borderRadius: 10, fontSize: 13, flexWrap: 'wrap' }}>
                <span>Total Visits: <strong>{entries.length}</strong></span>
                <span>Trees Monitored: <strong>{totalMonitored.toLocaleString()}</strong></span>
                {avgSurvival && <span>Avg Survival: <strong>{avgSurvival}%</strong></span>}
                <span>Photos: <strong>{totalPhotos}</strong></span>
              </div>
            );
          })()}
        </div>
        );
      })()}
      {/* Milestones */}
      <div className="card" style={{ marginBottom: 24 }}>
        <h4 style={{ marginTop: 0 }}>Milestones</h4>
        {milestones.length === 0 ? (
          <div className="empty-state">No milestones defined yet.</div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Due Date</th>
                <th>Status</th>
                <th>Plants Target</th>
              </tr>
            </thead>
            <tbody>
              {milestones.map(m => (
                <tr key={m.id}>
                  <td>{m.title}</td>
                  <td>{m.due_date ? new Date(m.due_date).toLocaleDateString() : '-'}</td>
                  <td><StatusBadge status={m.status} /></td>
                  <td>{(m.plants_target || 0).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Applications */}
      <div className="card" style={{ marginBottom: 24 }}>
        <h4 style={{ marginTop: 0 }}>Agency Applications</h4>
        {applications.length === 0 ? (
          <div className="empty-state">No applications received yet.</div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Agency</th>
                <th>Proposed Plants</th>
                <th>Status</th>
                <th>Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {applications.map(app => (
                <tr key={app.id}>
                  <td>{app.agency_name || `Agency #${app.agency_id}`}</td>
                  <td>{(app.proposed_plants || 0).toLocaleString()}</td>
                  <td><StatusBadge status={app.status} /></td>
                  <td>{app.created_at ? new Date(app.created_at).toLocaleDateString() : '-'}</td>
                  <td>
                    {app.status === 'pending' ? (
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button
                          className="btn btn-primary btn-sm"
                          disabled={actionLoading === app.id}
                          onClick={() => handleApplication(app.id, 'accept')}
                        >
                          Accept
                        </button>
                        <button
                          className="btn btn-secondary btn-sm"
                          disabled={actionLoading === app.id}
                          onClick={() => handleApplication(app.id, 'reject')}
                        >
                          Reject
                        </button>
                      </div>
                    ) : (
                      <span style={{ fontSize: 13, color: '#888' }}>-</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Allocations */}
      <div className="card" style={{ marginBottom: 24 }}>
        <h4 style={{ marginTop: 0 }}>Allocations</h4>
        {allocations.length === 0 ? (
          <div className="empty-state">No allocations yet.</div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Agency</th>
                <th>Allocated Plants</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {allocations.map(a => (
                <tr key={a.id}>
                  <td>{a.agency_name || `Agency #${a.agency_id}`}</td>
                  <td>{(a.allocated_plants || 0).toLocaleString()}</td>
                  <td><StatusBadge status={a.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Reports */}
      {target.reports && target.reports.length > 0 && (
        <div className="card" style={{ marginBottom: 24 }}>
          <h4 style={{ marginTop: 0 }}>Submitted Reports</h4>
          <table className="data-table">
            <thead>
              <tr><th>Type</th><th>Agency</th><th>Plant Count</th><th>Survival Rate</th><th>Status</th><th>Date</th></tr>
            </thead>
            <tbody>
              {target.reports.map(r => (
                <tr key={r.id}>
                  <td style={{ textTransform: 'capitalize' }}>{(r.report_type || '').replace(/_/g, ' ')}</td>
                  <td>{r.agency_name || '-'}</td>
                  <td>{r.plant_count?.toLocaleString() || '-'}</td>
                  <td>{r.survival_rate != null ? `${r.survival_rate}%` : '-'}</td>
                  <td><StatusBadge status={r.status} /></td>
                  <td>{r.submitted_at ? new Date(r.submitted_at).toLocaleDateString() : '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {lightboxPhotos && <PhotoLightbox photos={lightboxPhotos} initialIndex={lightboxIndex} entryInfo={lightboxEntry} onClose={() => setLightboxPhotos(null)} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Browse Agencies
// ---------------------------------------------------------------------------
function BrowseAgencies() {
  const [agencies, setAgencies] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const fetchAgencies = useCallback((p, q) => {
    setLoading(true);
    const params = { page: p, per_page: 12 };
    if (q) params.search = q;
    csrAPI.getAgencies(params)
      .then(res => {
        setAgencies(res.data.agencies || res.data.items || []);
        setPages(res.data.pages || 1);
      })
      .catch(() => toast.error('Failed to load agencies'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchAgencies(page, search); }, [page, search, fetchAgencies]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchAgencies(1, search);
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <h3 style={{ margin: 0 }}>Browse Implementing Agencies</h3>
        <form onSubmit={handleSearch} style={{ display: 'flex', gap: 8 }}>
          <input
            className="form-control"
            placeholder="Search agencies..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ minWidth: 200 }}
          />
          <button type="submit" className="btn btn-primary btn-sm">Search</button>
        </form>
      </div>

      {loading ? (
        <div className="empty-state">Loading agencies...</div>
      ) : agencies.length === 0 ? (
        <div className="empty-state">No agencies found.</div>
      ) : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 20 }}>
            {agencies.map(agency => (
              <div key={agency.id} className="card" style={{ padding: 24, borderLeft: '4px solid #2e7d32' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 14 }}>
                  <div style={{
                    width: 48, height: 48, borderRadius: '50%', background: '#e8f5e9',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 22, flexShrink: 0,
                  }}>{'\uD83C\uDFE2'}</div>
                  <div>
                    <h4 style={{ margin: 0, fontSize: 16, color: '#1b5e20' }}>
                      {agency.organisation_name || agency.name || 'Agency'}
                    </h4>
                    {(agency.city || agency.state) && (
                      <div style={{ fontSize: 13, color: '#888', marginTop: 2 }}>
                        {[agency.city, agency.state].filter(Boolean).join(', ')}
                      </div>
                    )}
                  </div>
                </div>
                <div style={{ fontSize: 14, color: '#555', lineHeight: 1.8 }}>
                  {agency.areas_of_operation && (
                    <div><strong>Areas:</strong> {agency.areas_of_operation}</div>
                  )}
                  {(agency.years_of_experience != null && agency.years_of_experience > 0) && (
                    <div><strong>Experience:</strong> {agency.years_of_experience} years</div>
                  )}
                  {(agency.rating != null && agency.rating > 0) && (
                    <div><strong>Rating:</strong> {'\u2B50'} {Number(agency.rating).toFixed(1)}</div>
                  )}
                  {(agency.total_trees_planted != null && agency.total_trees_planted > 0) && (
                    <div><strong>Trees Planted:</strong> {Number(agency.total_trees_planted).toLocaleString()}</div>
                  )}
                </div>
                <Link
                  to={`/csr/agencies/${agency.id}`}
                  className="btn btn-secondary btn-sm"
                  style={{ marginTop: 14, display: 'inline-block' }}
                >
                  View Profile
                </Link>
              </div>
            ))}
          </div>
          <Pagination page={page} pages={pages} onPageChange={setPage} />
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Agency Detail (CSR view)
// ---------------------------------------------------------------------------
function AgencyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [agency, setAgency] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    csrAPI.getAgency(id)
      .then(res => setAgency(res.data.agency || res.data))
      .catch(() => toast.error('Failed to load agency details'))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="loading-spinner">Loading...</div>;
  if (!agency) return <div className="empty-state">Agency not found.</div>;

  const profile = agency.agency_profile || {};

  return (
    <div>
      <button onClick={() => navigate('/csr/agencies')}
        style={{ background: 'none', border: 'none', color: '#2e7d32', cursor: 'pointer', fontSize: 14, fontWeight: 600, marginBottom: 20, padding: 0 }}>
        &larr; Back to Agencies
      </button>

      <div className="card" style={{ padding: 28, marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20 }}>
          <div style={{ width: 56, height: 56, borderRadius: '50%', background: '#e8f5e9', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, flexShrink: 0 }}>
            {'\uD83C\uDFE2'}
          </div>
          <div>
            <h2 style={{ margin: 0, color: '#1b5e20' }}>{agency.name || profile.organisation_name || 'Agency'}</h2>
            {(agency.city || agency.state) && (
              <div style={{ fontSize: 14, color: '#888', marginTop: 4 }}>{[agency.city, agency.state].filter(Boolean).join(', ')}</div>
            )}
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16, marginBottom: 20 }}>
          {agency.email && (
            <div style={{ background: '#f5f5f5', padding: 14, borderRadius: 10 }}>
              <div style={{ fontSize: 12, color: '#999', marginBottom: 4 }}>Email</div>
              <div style={{ fontWeight: 600, fontSize: 14 }}>{agency.email}</div>
            </div>
          )}
          {agency.phone && (
            <div style={{ background: '#f5f5f5', padding: 14, borderRadius: 10 }}>
              <div style={{ fontSize: 12, color: '#999', marginBottom: 4 }}>Phone</div>
              <div style={{ fontWeight: 600, fontSize: 14 }}>{agency.phone}</div>
            </div>
          )}
          {agency.address && (
            <div style={{ background: '#f5f5f5', padding: 14, borderRadius: 10 }}>
              <div style={{ fontSize: 12, color: '#999', marginBottom: 4 }}>Address</div>
              <div style={{ fontWeight: 600, fontSize: 14 }}>{agency.address}</div>
            </div>
          )}
          {agency.pincode && (
            <div style={{ background: '#f5f5f5', padding: 14, borderRadius: 10 }}>
              <div style={{ fontSize: 12, color: '#999', marginBottom: 4 }}>Pincode</div>
              <div style={{ fontWeight: 600, fontSize: 14 }}>{agency.pincode}</div>
            </div>
          )}
        </div>
      </div>

      {/* Agency Profile Details */}
      <div className="card" style={{ padding: 28 }}>
        <h3 style={{ marginTop: 0, color: '#1b5e20' }}>Agency Profile</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
          {profile.registration_number && (
            <div><strong>Registration No:</strong> {profile.registration_number}</div>
          )}
          {profile.areas_of_operation && (
            <div><strong>Areas of Operation:</strong> {profile.areas_of_operation}</div>
          )}
          {(profile.years_of_experience != null && profile.years_of_experience > 0) && (
            <div><strong>Experience:</strong> {profile.years_of_experience} years</div>
          )}
          {(profile.total_trees_planted != null && profile.total_trees_planted > 0) && (
            <div><strong>Trees Planted:</strong> {Number(profile.total_trees_planted).toLocaleString()}</div>
          )}
          {(profile.rating != null && profile.rating > 0) && (
            <div><strong>Rating:</strong> {'\u2B50'} {Number(profile.rating).toFixed(1)}</div>
          )}
          {profile.capacity_details && (
            <div style={{ gridColumn: '1 / -1' }}><strong>Capacity Details:</strong> {profile.capacity_details}</div>
          )}
          {profile.past_work_portfolio && (
            <div style={{ gridColumn: '1 / -1' }}><strong>Past Work Portfolio:</strong> {profile.past_work_portfolio}</div>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Legacy Data Upload Modal
// ---------------------------------------------------------------------------
function LegacyUploadModal({ onClose, onUploaded }) {
  const [form, setForm] = useState({
    title: '',
    description: '',
    plantation_year: '',
    location: '',
    total_plants: '',
    species: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.total_plants) {
      toast.error('Title and total plants are required');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        total_plants: Number(form.total_plants),
        plantation_year: form.plantation_year ? Number(form.plantation_year) : undefined,
      };
      await csrAPI.uploadLegacy(payload);
      toast.success('Legacy data uploaded successfully');
      onUploaded();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to upload legacy data');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 520 }}>
        <div className="modal-header">
          <h3 style={{ margin: 0 }}>Upload Legacy Data</h3>
          <button className="btn btn-secondary btn-sm" onClick={onClose}>&times;</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Title *</label>
            <input name="title" className="form-control" value={form.title} onChange={handleChange} required />
          </div>
          <div className="form-group">
            <label>Description</label>
            <textarea name="description" className="form-control" rows={3} value={form.description} onChange={handleChange} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="form-group">
              <label>Plantation Year</label>
              <input name="plantation_year" type="number" min="1900" max="2099" className="form-control" value={form.plantation_year} onChange={handleChange} placeholder="e.g. 2020" />
            </div>
            <div className="form-group">
              <label>Total Plants *</label>
              <input name="total_plants" type="number" min="1" className="form-control" value={form.total_plants} onChange={handleChange} required />
            </div>
          </div>
          <div className="form-group">
            <label>Location</label>
            <input name="location" className="form-control" value={form.location} onChange={handleChange} />
          </div>
          <div className="form-group">
            <label>Species</label>
            <input name="species" className="form-control" value={form.species} onChange={handleChange} placeholder="e.g. Neem, Teak, Bamboo" />
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? 'Uploading...' : 'Upload Data'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Legacy Data Page
// ---------------------------------------------------------------------------
function LegacyData() {
  const [records, setRecords] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);

  const fetchLegacy = useCallback((p) => {
    setLoading(true);
    csrAPI.getLegacy({ page: p, per_page: 10 })
      .then(res => {
        setRecords(res.data.records || res.data.legacy_data || res.data.items || []);
        setPages(res.data.pages || 1);
      })
      .catch(() => toast.error('Failed to load legacy data'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchLegacy(page); }, [page, fetchLegacy]);

  const handleUploaded = () => {
    setShowModal(false);
    setPage(1);
    fetchLegacy(1);
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h3 style={{ margin: 0 }}>Legacy Data</h3>
        <button className="btn btn-primary" onClick={() => setShowModal(true)}>+ Upload Legacy Data</button>
      </div>

      {loading ? (
        <div className="empty-state">Loading legacy data...</div>
      ) : records.length === 0 ? (
        <div className="empty-state">No legacy data uploaded yet.</div>
      ) : (
        <>
          <div className="card">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Year</th>
                  <th>Location</th>
                  <th>Plants</th>
                  <th>Species</th>
                  <th>Uploaded</th>
                </tr>
              </thead>
              <tbody>
                {records.map(r => (
                  <tr key={r.id}>
                    <td>{r.title}</td>
                    <td>{r.plantation_year || '-'}</td>
                    <td>{r.location || '-'}</td>
                    <td>{(r.total_plants || 0).toLocaleString()}</td>
                    <td>{r.species || '-'}</td>
                    <td>{r.created_at ? new Date(r.created_at).toLocaleDateString() : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination page={page} pages={pages} onPageChange={setPage} />
        </>
      )}

      {showModal && <LegacyUploadModal onClose={() => setShowModal(false)} onUploaded={handleUploaded} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers for adoption pages
// ---------------------------------------------------------------------------
function formatDate(dateStr) {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-IN', {
    year: 'numeric', month: 'short', day: 'numeric',
  });
}

function formatCurrency(amount) {
  if (amount == null) return '-';
  return new Intl.NumberFormat('en-IN', {
    style: 'currency', currency: 'INR', maximumFractionDigits: 0,
  }).format(amount);
}

// ---------------------------------------------------------------------------
// Reusable Modal wrapper (CSR)
// ---------------------------------------------------------------------------
function Modal({ open, title, onClose, children }) {
  if (!open) return null;
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{title}</h3>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>
        <div className="modal-body">{children}</div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Adopt Site modal form (CSR)
// ---------------------------------------------------------------------------
function CSRAdoptSiteModal({ open, onClose, onCreated, site }) {
  const [form, setForm] = useState({
    adoption_years: '1',
    branding_option: '',
    purpose: 'csr',
    dedication_message: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await adoptionAPI.createAdoption({
        site_id: site.id,
        adoption_years: parseInt(form.adoption_years),
        branding_option: form.branding_option || undefined,
        purpose: form.purpose,
        dedication_message: form.dedication_message || undefined,
      });
      toast.success('Site adopted successfully!');
      setForm({ adoption_years: '1', branding_option: '', purpose: 'csr', dedication_message: '' });
      onCreated();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create adoption');
    } finally {
      setSubmitting(false);
    }
  };

  if (!site) return null;
  const annualCost = site.annual_maintenance_cost || 0;
  const years = parseInt(form.adoption_years) || 1;
  const totalCost = annualCost * years;

  return (
    <Modal open={open} title={`Adopt: ${site.name}`} onClose={onClose}>
      <form onSubmit={handleSubmit} className="form">
        <div style={{ background: '#f0fdf4', borderRadius: 10, padding: 16, marginBottom: 16 }}>
          <div style={{ fontSize: 14, color: '#333' }}>
            <strong>{site.name}</strong> &mdash; {site.site_type?.replace(/_/g, ' ')}
          </div>
          <div style={{ fontSize: 13, color: '#666', marginTop: 4 }}>
            {[site.city, site.state].filter(Boolean).join(', ')} | {site.tree_count} trees
          </div>
          <div style={{ fontSize: 13, color: '#1a7431', fontWeight: 600, marginTop: 4 }}>
            {formatCurrency(annualCost)} / year
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>Adoption Duration (Years) *</label>
            <select name="adoption_years" value={form.adoption_years} onChange={handleChange} required>
              {Array.from(
                { length: (site.max_adoption_years || 5) - (site.min_adoption_years || 1) + 1 },
                (_, i) => (site.min_adoption_years || 1) + i
              ).map((y) => (
                <option key={y} value={y}>{y} {y === 1 ? 'year' : 'years'}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Purpose</label>
            <select name="purpose" value={form.purpose} onChange={handleChange}>
              <option value="csr">CSR</option>
              <option value="corporate_branding">Corporate Branding</option>
              <option value="community">Community</option>
              <option value="personal">Personal</option>
              <option value="memorial">Memorial</option>
            </select>
          </div>
        </div>
        <div className="form-group">
          <label>Branding Option</label>
          <input type="text" name="branding_option" value={form.branding_option} onChange={handleChange}
            placeholder="e.g. Company signboard, bench plaque" />
        </div>
        <div className="form-group">
          <label>Dedication Message</label>
          <textarea name="dedication_message" value={form.dedication_message} onChange={handleChange}
            rows={3} placeholder="Optional dedication message..." />
        </div>
        <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, padding: 16, marginBottom: 8 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: '#92400e' }}>
            Total Cost: {formatCurrency(totalCost)} for {years} {years === 1 ? 'year' : 'years'}
          </div>
        </div>
        <div className="form-actions">
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={submitting}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={submitting}>
            {submitting ? 'Confirming...' : 'Confirm Adoption'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Browse Adoption Sites (CSR)
// ---------------------------------------------------------------------------
function CSRBrowseAdoptionSites() {
  const [sites, setSites] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ city: '', state: '', site_type: '' });
  const [selectedSite, setSelectedSite] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, per_page: 9 };
      if (filters.city) params.city = filters.city;
      if (filters.state) params.state = filters.state;
      if (filters.site_type) params.site_type = filters.site_type;
      const res = await adoptionAPI.getSites(params);
      setSites(res.data.items || []);
      setPages(res.data.pages || 1);
    } catch {
      toast.error('Failed to load adoption sites');
    } finally {
      setLoading(false);
    }
  }, [page, filters]);

  useEffect(() => { load(); }, [load]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
    setPage(1);
  };

  return (
    <div>
      <div className="page-header"><h3>Adopt a Tree Site</h3></div>

      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <input name="city" placeholder="Filter by city..." value={filters.city}
          onChange={handleFilterChange} className="form-control" style={{ maxWidth: 200 }} />
        <input name="state" placeholder="Filter by state..." value={filters.state}
          onChange={handleFilterChange} className="form-control" style={{ maxWidth: 200 }} />
        <select name="site_type" value={filters.site_type} onChange={handleFilterChange} className="form-control" style={{ maxWidth: 200 }}>
          <option value="">All Site Types</option>
          <option value="park">Park</option>
          <option value="road_stretch">Road Stretch</option>
          <option value="tree_cluster">Tree Cluster</option>
          <option value="garden">Garden</option>
          <option value="campus">Campus</option>
        </select>
      </div>

      <CSRAdoptSiteModal open={showModal} onClose={() => setShowModal(false)} onCreated={() => { load(); }} site={selectedSite} />

      {loading ? (
        <div className="loading-spinner">Loading...</div>
      ) : sites.length === 0 ? (
        <p className="empty-state">No adoption sites available matching your filters.</p>
      ) : (
        <>
          <div className="cards-grid">
            {sites.map((site) => (
              <div key={site.id} className="card" style={{ padding: 20 }}>
                <h4 style={{ margin: '0 0 8px' }}>{site.name}</h4>
                <div style={{ marginBottom: 8 }}><StatusBadge status={site.site_type?.replace(/_/g, '-')} /></div>
                <p className="text-muted" style={{ fontSize: 14, marginBottom: 12 }}>
                  {site.description?.substring(0, 120)}{site.description?.length > 120 ? '...' : ''}
                </p>
                <div style={{ fontSize: 13, color: '#555', lineHeight: 1.8 }}>
                  <div><strong>Location:</strong> {[site.city, site.state].filter(Boolean).join(', ') || '-'}</div>
                  <div><strong>Trees:</strong> {site.tree_count || 0}</div>
                  <div><strong>Condition:</strong> <span style={{ textTransform: 'capitalize' }}>{site.current_condition?.replace(/_/g, ' ') || '-'}</span></div>
                  <div><strong>Annual Cost:</strong> {formatCurrency(site.annual_maintenance_cost)}</div>
                  <div><strong>Duration:</strong> {site.min_adoption_years || 1}-{site.max_adoption_years || 5} years</div>
                </div>
                <button className="btn btn-primary btn-sm" style={{ marginTop: 14, width: '100%' }}
                  onClick={() => { setSelectedSite(site); setShowModal(true); }}>
                  Adopt This Site
                </button>
              </div>
            ))}
          </div>
          <Pagination page={page} pages={pages} onPageChange={setPage} />
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// My Adoptions list (CSR)
// ---------------------------------------------------------------------------
function CSRMyAdoptionsList() {
  const [adoptions, setAdoptions] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adoptionAPI.getMyAdoptions({ page, per_page: 10 });
      setAdoptions(res.data.items || []);
      setPages(res.data.pages || 1);
    } catch {
      toast.error('Failed to load adoptions');
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <div className="page-header">
        <h3>My Adoptions</h3>
        <Link to="/csr/adopt" className="btn btn-primary">Adopt a Site</Link>
      </div>

      {loading ? (
        <div className="loading-spinner">Loading...</div>
      ) : adoptions.length === 0 ? (
        <div className="empty-state-container">
          <p className="empty-state">You have not adopted any tree sites yet.</p>
          <Link to="/csr/adopt" className="btn btn-primary">Browse Sites to Adopt</Link>
        </div>
      ) : (
        <>
          <div className="table-responsive">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Site Name</th>
                  <th>Location</th>
                  <th>Years</th>
                  <th>Total Amount</th>
                  <th>Payment</th>
                  <th>Status</th>
                  <th>Start Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {adoptions.map((a) => (
                  <tr key={a.id}>
                    <td>{a.site_name || '-'}</td>
                    <td>{[a.city, a.state].filter(Boolean).join(', ') || '-'}</td>
                    <td>{a.adoption_years}</td>
                    <td>{formatCurrency(a.total_amount)}</td>
                    <td><StatusBadge status={a.payment_status} /></td>
                    <td><StatusBadge status={a.adoption_status} /></td>
                    <td>{formatDate(a.start_date)}</td>
                    <td>
                      <button className="btn btn-secondary btn-sm"
                        onClick={() => navigate(`/csr/my-adoptions/${a.id}`)}>
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination page={page} pages={pages} onPageChange={setPage} />
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Adoption detail (CSR)
// ---------------------------------------------------------------------------
function CSRAdoptionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [adoption, setAdoption] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const res = await adoptionAPI.getAdoption(id);
        setAdoption(res.data.adoption || res.data);
      } catch {
        toast.error('Failed to load adoption details');
        navigate('/csr/my-adoptions');
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [id, navigate]);

  if (loading) return <div className="loading-spinner">Loading...</div>;
  if (!adoption) return null;

  const reports = adoption.reports || [];
  const site = adoption.site || {};

  return (
    <div>
      <button className="btn btn-secondary btn-sm" onClick={() => navigate('/csr/my-adoptions')} style={{ marginBottom: 16 }}>
        &larr; Back to Adoptions
      </button>

      <div className="card">
        <div className="card-header">
          <h3>Adoption #{adoption.id} &mdash; {adoption.site_name || site.name || 'Site'}</h3>
          <StatusBadge status={adoption.adoption_status} />
        </div>
        <div className="detail-grid">
          <div className="detail-item"><span className="detail-label">Site Name</span><span className="detail-value">{adoption.site_name || site.name || '-'}</span></div>
          <div className="detail-item"><span className="detail-label">Site Type</span><span className="detail-value" style={{ textTransform: 'capitalize' }}>{(adoption.site_type || site.site_type || '-').replace(/_/g, ' ')}</span></div>
          <div className="detail-item"><span className="detail-label">Location</span><span className="detail-value">{[adoption.city || site.city, adoption.state || site.state].filter(Boolean).join(', ') || '-'}</span></div>
          <div className="detail-item"><span className="detail-label">Trees</span><span className="detail-value">{adoption.tree_count || site.tree_count || 0}</span></div>
          <div className="detail-item"><span className="detail-label">Duration</span><span className="detail-value">{adoption.adoption_years} year(s)</span></div>
          <div className="detail-item"><span className="detail-label">Period</span><span className="detail-value">{formatDate(adoption.start_date)} to {formatDate(adoption.end_date)}</span></div>
          <div className="detail-item"><span className="detail-label">Annual Amount</span><span className="detail-value">{formatCurrency(adoption.annual_amount)}</span></div>
          <div className="detail-item"><span className="detail-label">Total Amount</span><span className="detail-value">{formatCurrency(adoption.total_amount)}</span></div>
          <div className="detail-item"><span className="detail-label">Payment Status</span><span className="detail-value"><StatusBadge status={adoption.payment_status} /></span></div>
          <div className="detail-item"><span className="detail-label">Purpose</span><span className="detail-value" style={{ textTransform: 'capitalize' }}>{(adoption.purpose || '-').replace(/_/g, ' ')}</span></div>
          <div className="detail-item"><span className="detail-label">Branding</span><span className="detail-value">{adoption.branding_option || '-'}</span></div>
          <div className="detail-item"><span className="detail-label">Dedication</span><span className="detail-value">{adoption.dedication_message || '-'}</span></div>
          <div className="detail-item"><span className="detail-label">Next Report Due</span><span className="detail-value">{formatDate(adoption.next_report_due)}</span></div>
          <div className="detail-item"><span className="detail-label">Reports Received</span><span className="detail-value">{adoption.reports_received || 0}</span></div>
        </div>
      </div>

      {site.name && (
        <div className="card" style={{ marginTop: 24 }}>
          <div className="card-header"><h3>Site Details</h3></div>
          <p>{site.description || 'No description available.'}</p>
          {site.amenities && <p style={{ marginTop: 8 }}><strong>Amenities:</strong> {site.amenities}</p>}
          {site.address && <p style={{ marginTop: 8 }}><strong>Address:</strong> {site.address}</p>}
        </div>
      )}

      <div className="card" style={{ marginTop: 24 }}>
        <div className="card-header"><h3>Adoption Reports</h3></div>
        {reports.length === 0 ? (
          <p className="empty-state">No adoption reports available yet.</p>
        ) : (
          <div className="reports-grid">
            {reports.map((r) => (
              <div key={r.id} className="report-card">
                <div className="report-info">
                  <p><strong>{r.report_quarter} &mdash; {(r.report_type || '').replace(/_/g, ' ')}</strong></p>
                  <p className="text-muted">{r.description || r.maintenance_activities || ''}</p>
                  <div className="report-meta">
                    <span>{formatDate(r.submitted_at)}</span>
                    <StatusBadge status={r.status} />
                    <span>Health: <span style={{ textTransform: 'capitalize' }}>{r.tree_health_status || '-'}</span></span>
                  </div>
                  <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                    Healthy: {r.trees_healthy || 0} | Attention: {r.trees_needing_attention || 0} | Replaced: {r.trees_replaced || 0}
                  </div>
                  {r.report_document && (
                    <a href={r.report_document} target="_blank" rel="noopener noreferrer"
                      style={{ display: 'inline-block', marginTop: 6, color: '#2e7d32', fontWeight: 500, fontSize: 12 }}>
                      📄 View Report PDF
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// CSR Post Idea Modal
// ---------------------------------------------------------------------------
const CSR_INITIAL_IDEA_FORM = { title: '', description: '', category: '' };

function CSRPostIdeaModal({ open, onClose, onCreated }) {
  const [form, setForm] = useState(CSR_INITIAL_IDEA_FORM);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.description) {
      toast.warn('Please provide a title and description');
      return;
    }
    setSubmitting(true);
    try {
      await csrAPI.postIdea(form);
      toast.success('Idea posted successfully!');
      setForm(CSR_INITIAL_IDEA_FORM);
      onCreated();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to post idea');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open={open} title="Post New Idea" onClose={onClose}>
      <form onSubmit={handleSubmit} className="form">
        <div className="form-group">
          <label>Title *</label>
          <input type="text" name="title" value={form.title} onChange={handleChange} placeholder="Give your idea a clear title" required />
        </div>
        <div className="form-group">
          <label>Description *</label>
          <textarea name="description" value={form.description} onChange={handleChange} rows={5} placeholder="Describe your idea in detail..." required />
        </div>
        <div className="form-group">
          <label>Category</label>
          <input type="text" name="category" value={form.category} onChange={handleChange} placeholder="e.g. CSR Initiative, Corporate Partnership, Community" />
        </div>
        <div className="form-actions">
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={submitting}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={submitting}>
            {submitting ? 'Posting...' : 'Post Idea'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// CSR Ideas List
// ---------------------------------------------------------------------------
function CSRIdeas() {
  const [ideas, setIdeas] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await csrAPI.getMyIdeas({ page, per_page: 10 });
      setIdeas(res.data.ideas || res.data.items || []);
      setPages(res.data.pages || 1);
    } catch {
      toast.error('Failed to load ideas');
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <div className="page-header">
        <h3>Collaboration Ideas</h3>
        <button className="btn btn-primary" onClick={() => setShowModal(true)}>Post New Idea</button>
      </div>

      <CSRPostIdeaModal open={showModal} onClose={() => setShowModal(false)} onCreated={() => { setPage(1); load(); }} />

      {loading ? (
        <div className="loading-spinner">Loading...</div>
      ) : ideas.length === 0 ? (
        <div className="empty-state-container">
          <p className="empty-state">You have not posted any ideas yet.</p>
          <button className="btn btn-primary" onClick={() => setShowModal(true)}>Share Your First Idea</button>
        </div>
      ) : (
        <>
          <div className="cards-grid">
            {ideas.map((idea) => (
              <div key={idea.id} className="idea-card">
                <div className="idea-card-header">
                  <h4>{idea.title}</h4>
                  <StatusBadge status={idea.status} />
                </div>
                <p className="text-muted">
                  {idea.description?.substring(0, 200)}{idea.description?.length > 200 ? '...' : ''}
                </p>
                <div className="idea-meta">
                  {idea.category && <span className="idea-category">{idea.category}</span>}
                  <span className="text-muted">{formatDate(idea.created_at)}</span>
                </div>
              </div>
            ))}
          </div>
          <Pagination page={page} pages={pages} onPageChange={setPage} />
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Monitoring Progress (GPS Map, Zone Cards, Filtered Stats, Photos)
// ---------------------------------------------------------------------------
function MonitoringOverview() {
  const HISTORY_PER_PAGE = 5;
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedZoneId, setSelectedZoneId] = useState(null);
  const [selectedTargetId, setSelectedTargetId] = useState('');
  const [historyPage, setHistoryPage] = useState(1);
  const [lightboxPhotos, setLightboxPhotos] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [lightboxEntry, setLightboxEntry] = useState('');

  useEffect(() => { setHistoryPage(1); }, [selectedTargetId, selectedZoneId]);

  useEffect(() => {
    csrAPI.getMonitoringSummary()
      .then(res => setSummary(res.data))
      .catch(() => toast.error('Failed to load monitoring summary'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="loading-spinner">Loading monitoring data...</div>;

  const allZones = summary.monitoring_zones || [];
  const allEntries = summary.monitoring_entries || [];
  const targetsInfo = summary.targets_info || [];

  // Build target name lookup
  const targetNameMap = {};
  targetsInfo.forEach(t => { targetNameMap[t.id] = t.title; });

  // Filter by selected project
  const zones = selectedTargetId ? allZones.filter(z => z.target_id === parseInt(selectedTargetId)) : allZones;
  const entries = selectedTargetId ? allEntries.filter(e => e.target_id === parseInt(selectedTargetId)) : allEntries;

  // Approved-only, dedupe to ONE latest entry per zone
  const approvedEntries = entries.filter(e => e.status === 'approved');
  const _byZone = {};
  approvedEntries.forEach(e => {
    if (!e.zone_id || !e.monitoring_date) return;
    const cur = _byZone[e.zone_id];
    if (!cur || e.monitoring_date > cur.monitoring_date ||
        (e.monitoring_date === cur.monitoring_date && (e.id || 0) > (cur.id || 0))) {
      _byZone[e.zone_id] = e;
    }
  });
  const latestEntries = [...Object.values(_byZone), ...approvedEntries.filter(e => !e.zone_id)];
  const fHealthy = latestEntries.reduce((s, e) => s + (e.trees_healthy || 0), 0);
  const fAttention = latestEntries.reduce((s, e) => s + (e.trees_needs_attention || 0), 0);
  const fCritical = latestEntries.reduce((s, e) => s + (e.trees_critical || 0), 0);
  const fDead = latestEntries.reduce((s, e) => s + (e.trees_dead || 0), 0);
  // Trees Monitored = reporter-entered total (may exceed sum of health buckets if some trees are unclassified)
  const fTotalMonitored = latestEntries.reduce((s, e) => s + (e.total_trees_monitored || 0), 0);
  // Survival = (Healthy + Need Attention + Critical) / target planted total. Target = sum of total_plants for selected projects.
  const filteredTargets = selectedTargetId
    ? targetsInfo.filter(t => t.id === parseInt(selectedTargetId))
    : targetsInfo;
  const fTargetTotal = filteredTargets.reduce((s, t) => s + (t.total_plants || 0), 0);
  const fAliveTrees = fHealthy + fAttention + fCritical;
  const fAvgSurvival = fTargetTotal > 0 ? ((fAliveTrees / fTargetTotal) * 100).toFixed(2) : 0;
  const fAgencies = new Set(entries.map(e => e.agency_user_id)).size;
  const fTargets = new Set(entries.map(e => e.target_id)).size;
  const fPhotos = entries.reduce((s, e) => s + (e.photos?.length || 0), 0);

  // Per-zone tree count from the latest approved entry
  const zoneTreeCount = {};
  latestEntries.forEach(e => {
    if (e.zone_id) zoneTreeCount[e.zone_id] = e.total_trees_monitored || 0;
  });

  const statusColors = { pending: '#ff9800', approved: '#4caf50', disbursed: '#2196f3', submitted: '#ff9800', rejected: '#ef5350', under_review: '#ff9800', draft: '#9e9e9e' };
  const healthColors = { excellent: '#1b5e20', good: '#4caf50', fair: '#ff9800', poor: '#f44336', critical: '#b71c1c' };
  const zoneColors = ['#1b5e20', '#0d47a1', '#e65100', '#6a1b9a', '#c62828', '#00838f', '#ad1457', '#4e342e'];
  const zoneColorMap = {};
  zones.forEach((z, i) => { zoneColorMap[z.id] = zoneColors[i % zoneColors.length]; });

  const fmt = n => (n || 0).toLocaleString();
  const pct = (v, max) => max > 0 ? `${((v / max) * 100).toFixed(1)}%` : '0%';

  // Collect map points
  const allPoints = [];
  zones.forEach(z => {
    if (z.boundary_polygon) z.boundary_polygon.forEach(p => allPoints.push([p.lat, p.lng]));
    if (z.center_latitude && z.center_longitude) allPoints.push([z.center_latitude, z.center_longitude]);
  });
  entries.forEach(e => {
    if (e.latitude && e.longitude) allPoints.push([e.latitude, e.longitude]);
    (e.photos || []).forEach(p => { if (p.latitude && p.longitude) allPoints.push([p.latitude, p.longitude]); });
  });

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <h3 style={{ margin: 0, color: '#1b5e20' }}>Monitoring Progress</h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <label htmlFor="csr-project-filter" style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>Project:</label>
          <select id="csr-project-filter" value={selectedTargetId} onChange={e => { setSelectedTargetId(e.target.value); setSelectedZoneId(null); }}
            style={{ padding: '10px 16px', borderRadius: 10, border: '2px solid #1b5e20', background: '#fff', fontSize: 14, minWidth: 260, fontWeight: 500, color: '#1b5e20', cursor: 'pointer' }}>
            <option value="">All Projects ({targetsInfo.length})</option>
            {targetsInfo.map(t => (
              <option key={t.id} value={t.id}>{t.title} — {[t.city, t.state].filter(Boolean).join(', ')}</option>
            ))}
          </select>
        </div>
      </div>

      {/* KPI Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16, marginBottom: 24 }}>
        {[
          { label: 'Trees Monitored', value: fmt(fTotalMonitored), sub: `${fTargets} targets`, color: '#4caf50', icon: '\uD83C\uDF33' },
          { label: 'Avg Survival Rate', value: `${fAvgSurvival}%`, sub: `${entries.length} entries`, color: fAvgSurvival >= 80 ? '#4caf50' : '#ff9800', icon: '\uD83D\uDCC8' },
          { label: 'Agencies Reporting', value: fmt(fAgencies), sub: `${fmt(fPhotos)} photos`, color: '#1976d2', icon: '\uD83C\uDFE2' },
          { label: 'Trees Healthy', value: fmt(fHealthy), sub: pct(fHealthy, fTotalMonitored) + ' of monitored', color: '#2e7d32', icon: '\u2705' },
        ].map((kpi, i) => (
          <div key={i} style={{ background: '#e8f5e9', padding: 16, borderRadius: 12, textAlign: 'center' }}>
            <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>{kpi.icon} {kpi.label}</div>
            <div style={{ fontSize: 24, fontWeight: 700, color: '#1b5e20' }}>{kpi.value}</div>
            <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>{kpi.sub}</div>
          </div>
        ))}
      </div>

      {/* Tree Health Summary */}
      <div className="card" style={{ padding: 28, marginBottom: 24 }}>
        <h3 style={{ marginTop: 0, color: '#1b5e20' }}>Tree Health Summary</h3>
        <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', fontSize: 13 }}>
          <span>Healthy: <strong style={{ color: '#4caf50' }}>{fmt(fHealthy)}</strong></span>
          <span>Needs Attention: <strong style={{ color: '#ff9800' }}>{fmt(fAttention)}</strong></span>
          <span>Critical: <strong style={{ color: '#f44336' }}>{fmt(fCritical)}</strong></span>
          <span>Dead: <strong style={{ color: '#999' }}>{fmt(fDead)}</strong></span>
          <span>Total: <strong>{fmt(fTotalMonitored)}</strong></span>
          {fTotalMonitored > 0 && <span>Survival: <strong style={{ color: '#1b5e20' }}>{fAvgSurvival}%</strong></span>}
        </div>
      </div>

      {/* GPS Map */}
      {allPoints.length > 0 && (() => {
        const bounds = L.latLngBounds(allPoints);
        return (
          <div className="card" style={{ padding: 28, marginBottom: 24 }}>
            <h3 style={{ marginTop: 0, color: '#1b5e20' }}>Monitoring & GPS Mapping</h3>
            <div style={{ height: 450, borderRadius: 12, overflow: 'hidden', border: '1px solid #e0e0e0' }}>
              <MapContainer bounds={bounds.pad(0.1)} style={{ height: '100%', width: '100%' }} scrollWheelZoom={true}>
                <TileLayer url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}" attribution="Esri" />
                <ZoneFlyTo zones={zones} entries={entries} selectedZoneId={selectedZoneId} />
                {zones.map((z) => z.boundary_polygon && (
                  <Polygon key={z.id} positions={z.boundary_polygon.map(p => [p.lat, p.lng])}
                    pathOptions={{ color: zoneColorMap[z.id], weight: selectedZoneId === z.id ? 5 : 3, fillOpacity: selectedZoneId === z.id ? 0.35 : 0.2 }}>
                    <Popup><strong>{z.name}</strong><br />{(zoneTreeCount[z.id] || z.tree_count || 0).toLocaleString()} trees{z.area_sqm ? ` | ${Number(z.area_sqm).toLocaleString()} sq m` : ''}</Popup>
                  </Polygon>
                ))}
                {entries.flatMap(e => (e.photos || []).filter(p => p.latitude && p.longitude).map(p => {
                  const markerColor = zoneColorMap[e.zone_id] || '#e65100';
                  const isSelected = selectedZoneId && e.zone_id === selectedZoneId;
                  const size = isSelected ? 16 : 12;
                  return (
                    <Marker key={`photo-${p.id}`} position={[p.latitude, p.longitude]}
                      icon={L.divIcon({ className: '', html: `<div style="background:${markerColor};border-radius:50%;width:${size}px;height:${size}px;border:2px solid #fff;box-shadow:0 0 4px rgba(0,0,0,0.4);${isSelected ? 'animation:pulse 1s infinite;' : ''}"></div>`, iconSize: [size, size], iconAnchor: [size/2, size/2] })}>
                      <Popup>
                        <div style={{ textAlign: 'center' }}>
                          <img src={getPhotoUrl(p.photo_url)} alt={p.photo_type} style={{ width: 150, borderRadius: 6 }} />
                          <div style={{ fontSize: 11, marginTop: 4, textTransform: 'capitalize' }}>{p.photo_type} | {e.zone_name}</div>
                        </div>
                      </Popup>
                    </Marker>
                  );
                }))}
              </MapContainer>
            </div>
            {/* Clickable zone legend */}
            <div style={{ display: 'flex', gap: 12, marginTop: 12, flexWrap: 'wrap', fontSize: 12 }}>
              <span onClick={() => setSelectedZoneId(null)}
                style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', padding: '4px 10px', borderRadius: 8, background: !selectedZoneId ? '#e8f5e9' : '#fff', border: !selectedZoneId ? '2px solid #4caf50' : '1px solid #e0e0e0', fontWeight: !selectedZoneId ? 700 : 400 }}>
                All Zones
              </span>
              {zones.map((z) => (
                <span key={z.id} onClick={() => setSelectedZoneId(selectedZoneId === z.id ? null : z.id)}
                  style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', padding: '4px 10px', borderRadius: 8, background: selectedZoneId === z.id ? zoneColorMap[z.id] + '22' : '#fff', border: selectedZoneId === z.id ? `2px solid ${zoneColorMap[z.id]}` : '1px solid #e0e0e0', fontWeight: selectedZoneId === z.id ? 700 : 400 }}>
                  <span style={{ width: 12, height: 12, borderRadius: 3, background: zoneColorMap[z.id], display: 'inline-block' }} />
                  {z.name} ({(zoneTreeCount[z.id] || z.tree_count || 0).toLocaleString()} trees)
                </span>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Monitoring Zone Cards */}
      {zones.length > 0 && (
        <div className="card" style={{ padding: 28, marginBottom: 24 }}>
          <h3 style={{ marginTop: 0, color: '#1b5e20' }}>Monitoring Zones <span style={{ fontSize: 13, fontWeight: 400, color: '#888' }}>— click a zone to see it on the map</span></h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
            {zones.map((z, i) => {
              const isActive = selectedZoneId === z.id;
              const zColor = zoneColors[i % zoneColors.length];
              return (
              <div key={z.id} onClick={() => { setSelectedZoneId(isActive ? null : z.id); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                style={{ border: isActive ? `2px solid ${zColor}` : '1px solid #e0e0e0', borderRadius: 12, padding: 16, background: isActive ? zColor + '0a' : '#fafafa', cursor: 'pointer', transition: 'all 0.25s', transform: isActive ? 'scale(1.02)' : 'scale(1)', boxShadow: isActive ? `0 4px 16px ${zColor}33` : 'none' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <span style={{ width: 14, height: 14, borderRadius: 4, background: zColor, display: 'inline-block', flexShrink: 0 }} />
                  <span style={{ fontWeight: 700, color: '#1b5e20', fontSize: 15 }}>{z.name}</span>
                  {isActive && <span style={{ marginLeft: 'auto', fontSize: 11, background: zColor, color: '#fff', padding: '2px 8px', borderRadius: 10 }}>Viewing on map</span>}
                </div>
                {z.description && <div style={{ fontSize: 13, color: '#666', marginBottom: 8 }}>{z.description}</div>}
                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 13 }}>
                  <span>Trees: <strong>{(zoneTreeCount[z.id] || z.tree_count || 0).toLocaleString()}</strong></span>
                  {z.area_sqm && <span>Area: <strong>{Number(z.area_sqm).toLocaleString()}</strong> sq m</span>}
                  <span style={{ textTransform: 'capitalize', color: '#888' }}>{z.zone_type?.replace('_', ' ')}</span>
                </div>
                {z.boundary_polygon ? (
                  <div style={{ marginTop: 8, fontSize: 12, color: '#4caf50', fontWeight: 600 }}>GPS Boundary Mapped ({z.boundary_polygon.length} points)</div>
                ) : (
                  <div style={{ marginTop: 8, fontSize: 12, color: '#ff9800' }}>GPS boundary not mapped yet</div>
                )}
                <div style={{ marginTop: 6, fontSize: 12, color: '#999' }}>{z.entries_count || 0} monitoring {(z.entries_count || 0) === 1 ? 'entry' : 'entries'}</div>
              </div>
            );})}
          </div>
        </div>
      )}

      {/* Monitoring Entries with Photos */}
      {entries.length > 0 && (() => {
        const totalPhotos = entries.reduce((s, e) => s + (e.photos?.length || 0), 0);
        return (
          <div style={{ display: 'flex', gap: 24, marginBottom: 20, padding: '12px 16px', background: '#f5f5f5', borderRadius: 10, fontSize: 13, flexWrap: 'wrap' }}>
            <span>Total Visits: <strong>{entries.length}</strong></span>
            <span>Trees Monitored: <strong>{fTotalMonitored.toLocaleString()}</strong></span>
            {fAvgSurvival > 0 && <span>Avg Survival: <strong>{fAvgSurvival}%</strong></span>}
            <span>Photos: <strong>{totalPhotos}</strong></span>
          </div>
        );
      })()}

      {entries.length > 0 && (() => {
        const historyPages = Math.max(1, Math.ceil(entries.length / HISTORY_PER_PAGE));
        const currentPage = Math.min(historyPage, historyPages);
        const pagedEntries = entries.slice((currentPage - 1) * HISTORY_PER_PAGE, currentPage * HISTORY_PER_PAGE);
        return (
        <div className="card" style={{ padding: 28, marginBottom: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
            <h3 style={{ margin: 0, color: '#1b5e20' }}>Monitoring History</h3>
            <span style={{ fontSize: 12, color: '#888' }}>
              Showing {(currentPage - 1) * HISTORY_PER_PAGE + 1}&ndash;{Math.min(currentPage * HISTORY_PER_PAGE, entries.length)} of {entries.length}
            </span>
          </div>
          {pagedEntries.map(e => {
            const photos = e.photos || [];
            return (
              <div key={e.id} style={{ border: '1px solid #e8e8e8', borderRadius: 12, padding: 16, marginBottom: 16, background: '#fafffe', marginTop: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
                  <div>
                    {!selectedTargetId && targetNameMap[e.target_id] && (
                      <div style={{ fontSize: 11, color: '#1976d2', fontWeight: 600, marginBottom: 3, background: '#e3f2fd', display: 'inline-block', padding: '2px 8px', borderRadius: 6 }}>
                        {targetNameMap[e.target_id]}
                      </div>
                    )}
                    <div>
                      <strong style={{ fontSize: 14 }}>{e.zone_name || 'Unknown Zone'}</strong>
                      <span style={{ fontSize: 12, color: '#888', marginLeft: 8 }}>{e.monitoring_date ? new Date(e.monitoring_date).toLocaleDateString() : ''}</span>
                      {e.agency_name && <span style={{ fontSize: 12, color: '#666', marginLeft: 8 }}>by {e.agency_name}</span>}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <span style={{ padding: '3px 10px', borderRadius: 10, fontSize: 12, fontWeight: 600, color: healthColors[e.overall_health] || '#666', background: (healthColors[e.overall_health] || '#666') + '18', textTransform: 'capitalize' }}>{e.overall_health}</span>
                    <span style={{ padding: '3px 10px', borderRadius: 10, fontSize: 12, fontWeight: 600, color: statusColors[e.status] || '#666', background: (statusColors[e.status] || '#666') + '18' }}>{e.status}</span>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 20, marginTop: 10, fontSize: 13, flexWrap: 'wrap' }}>
                  <span>Trees: <strong>{(e.total_trees_monitored || 0).toLocaleString()}</strong></span>
                  <span>Healthy: <strong style={{ color: '#4caf50' }}>{e.trees_healthy || 0}</strong></span>
                  <span>Attention: <strong style={{ color: '#ff9800' }}>{e.trees_needs_attention || 0}</strong></span>
                  <span>Critical: <strong style={{ color: '#f44336' }}>{e.trees_critical || 0}</strong></span>
                  <span>Dead: <strong style={{ color: '#999' }}>{e.trees_dead || 0}</strong></span>
                  {e.survival_rate != null && <span>Survival: <strong>{e.survival_rate}%</strong></span>}
                </div>
                {e.notes && <div style={{ marginTop: 8, fontSize: 13, color: '#555', fontStyle: 'italic' }}>{e.notes}</div>}
                {e.latitude && e.longitude && (
                  <div style={{ marginTop: 8, fontSize: 11, color: '#1976d2' }}>
                    📍 Entry GPS: {Number(e.latitude).toFixed(6)}, {Number(e.longitude).toFixed(6)}
                  </div>
                )}
                {photos.length > 0 && (
                  <div style={{ marginTop: 12 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#666', marginBottom: 8 }}>Photos ({photos.length})</div>
                    <div style={{ display: 'flex', gap: 10, overflowX: 'auto', paddingBottom: 4 }}>
                      {photos.map((p, pIdx) => (
                        <div key={p.id} onClick={() => { setLightboxPhotos(photos); setLightboxIndex(pIdx); setLightboxEntry(e.zone_name || ''); }}
                          style={{ flexShrink: 0, textAlign: 'center', cursor: 'pointer' }}>
                          <div style={{ position: 'relative', width: 100, height: 75 }}>
                            <img src={getPhotoUrl(p.photo_url)} alt={p.photo_type}
                              style={{ width: 100, height: 75, objectFit: 'cover', borderRadius: 8, border: '2px solid #e0e0e0' }}
                              onError={ev => { ev.target.style.display = 'none'; }} />
                            {p.latitude && p.longitude && (
                              <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'rgba(0,0,0,0.65)', color: '#fff', fontSize: 8, padding: '2px 3px', borderRadius: '0 0 6px 6px', lineHeight: 1.2, textAlign: 'center' }}>
                                {Number(p.latitude).toFixed(5)}, {Number(p.longitude).toFixed(5)}
                              </div>
                            )}
                          </div>
                          <div style={{ fontSize: 10, color: '#888', marginTop: 2, textTransform: 'capitalize' }}>{p.photo_type}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          {historyPages > 1 && (
            <div style={{ marginTop: 16 }}>
              <Pagination page={currentPage} pages={historyPages} onPageChange={setHistoryPage} />
            </div>
          )}
        </div>
        );
      })()}

      {entries.length === 0 && (
        <div style={{ textAlign: 'center', padding: 60, background: '#fff', borderRadius: 14, boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>{'\uD83D\uDCCA'}</div>
          <h3 style={{ color: '#1b5e20', margin: '0 0 8px' }}>No Monitoring Data Yet</h3>
          <p style={{ color: '#888' }}>Monitoring entries will appear here once agencies start submitting field reports.</p>
        </div>
      )}
      {lightboxPhotos && <PhotoLightbox photos={lightboxPhotos} initialIndex={lightboxIndex} entryInfo={lightboxEntry} onClose={() => setLightboxPhotos(null)} />}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Monitoring Entry Detail
// ---------------------------------------------------------------------------
function MonitoringEntryDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [entry, setEntry] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    csrAPI.getMonitoringEntry(id)
      .then(res => setEntry(res.data.entry || res.data))
      .catch(() => { toast.error('Failed to load entry'); navigate('/csr/monitoring'); })
      .finally(() => setLoading(false));
  }, [id, navigate]);

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', padding: '60px' }}><div className="spinner" /></div>;
  if (!entry) return null;

  const total = entry.total_trees_monitored || 0;
  const healthyPct = total > 0 ? ((entry.trees_healthy / total) * 100).toFixed(1) : 0;
  const attentionPct = total > 0 ? ((entry.trees_needs_attention / total) * 100).toFixed(1) : 0;
  const criticalPct = total > 0 ? ((entry.trees_critical / total) * 100).toFixed(1) : 0;
  const deadPct = total > 0 ? ((entry.trees_dead / total) * 100).toFixed(1) : 0;

  const miniHealthData = {
    labels: ['Healthy', 'Needs Attention', 'Critical', 'Dead'],
    datasets: [{
      data: [entry.trees_healthy || 0, entry.trees_needs_attention || 0, entry.trees_critical || 0, entry.trees_dead || 0],
      backgroundColor: ['#4caf50', '#ff9800', '#f44336', '#9e9e9e'], borderWidth: 0,
    }],
  };

  const healthBadge = (status) => {
    const c = { healthy: '#4caf50', needs_attention: '#ff9800', critical: '#f44336', dead: '#9e9e9e' };
    const lbl = (status || '').replace('_', ' ').replace(/\b\w/g, ch => ch.toUpperCase());
    return <span style={{ padding: '3px 10px', borderRadius: '10px', fontSize: '11px', fontWeight: 600, color: c[status] || '#6b7280', background: `${c[status] || '#6b7280'}18` }}>{lbl || '-'}</span>;
  };

  const apiBase = window.location.origin.includes('localhost') ? 'https://haritanjali.org' : '';

  return (
    <div>
      {/* Back Button */}
      <button onClick={() => navigate('/csr/monitoring')}
        style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 16px', border: '1px solid #e5e7eb',
          borderRadius: '10px', background: '#fff', cursor: 'pointer', fontSize: '13px', fontWeight: 500, color: '#374151', marginBottom: '20px' }}>
        ← Back to Monitoring
      </button>

      {/* Header Card */}
      <div style={{
        background: 'linear-gradient(135deg, #1a7431, #2e7d32)', color: '#fff',
        padding: '24px 32px', borderRadius: '16px', marginBottom: '24px',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '20px' }}>{entry.zone_name || 'Ad-hoc Monitoring'}</h2>
            <p style={{ margin: '6px 0 0', opacity: 0.9, fontSize: '14px' }}>
              {entry.target_title || `Target #${entry.target_id}`} • {entry.agency_name || 'Agency'}
            </p>
            <p style={{ margin: '4px 0 0', opacity: 0.7, fontSize: '13px' }}>
              {entry.monitoring_date ? new Date(entry.monitoring_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' }) : '-'}
            </p>
          </div>
          <StatusBadge status={entry.status} />
        </div>
      </div>

      {/* Tree Health Section */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '22px', marginBottom: '24px' }}>
        {/* Health breakdown */}
        <div style={{ background: '#fff', borderRadius: '14px', padding: '24px', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
          <div style={{ fontSize: '16px', fontWeight: 600, marginBottom: '16px', paddingBottom: '12px', borderBottom: '1px solid #f0f0f0' }}>
            Tree Health Breakdown
          </div>
          <div style={{ textAlign: 'center', marginBottom: '20px' }}>
            <span style={{ fontSize: '36px', fontWeight: 800, color: '#1a7431' }}>{total.toLocaleString()}</span>
            <span style={{ fontSize: '14px', color: '#6b7280', marginLeft: '8px' }}>Trees Monitored</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '20px' }}>
            {[
              { label: 'Healthy', value: entry.trees_healthy || 0, pct: healthyPct, color: '#4caf50', icon: '✅' },
              { label: 'Attention', value: entry.trees_needs_attention || 0, pct: attentionPct, color: '#ff9800', icon: '⚠️' },
              { label: 'Critical', value: entry.trees_critical || 0, pct: criticalPct, color: '#f44336', icon: '🔴' },
              { label: 'Dead', value: entry.trees_dead || 0, pct: deadPct, color: '#9e9e9e', icon: '💀' },
            ].map(s => (
              <div key={s.label} style={{ textAlign: 'center', padding: '14px 8px', background: `${s.color}10`, borderRadius: '12px' }}>
                <div style={{ fontSize: '20px', marginBottom: '4px' }}>{s.icon}</div>
                <div style={{ fontSize: '22px', fontWeight: 700, color: s.color }}>{s.value.toLocaleString()}</div>
                <div style={{ fontSize: '11px', color: '#6b7280' }}>{s.label} ({s.pct}%)</div>
              </div>
            ))}
          </div>
          {/* Stacked health bar */}
          <div style={{ height: '12px', borderRadius: '6px', overflow: 'hidden', display: 'flex', background: '#f3f4f6' }}>
            {total > 0 && <div style={{ width: `${healthyPct}%`, background: '#4caf50' }} />}
            {total > 0 && <div style={{ width: `${attentionPct}%`, background: '#ff9800' }} />}
            {total > 0 && <div style={{ width: `${criticalPct}%`, background: '#f44336' }} />}
            {total > 0 && <div style={{ width: `${deadPct}%`, background: '#9e9e9e' }} />}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '16px', padding: '12px 16px', background: '#f0fdf4', borderRadius: '10px' }}>
            <div><span style={{ fontSize: '13px', color: '#6b7280' }}>Survival Rate</span><br /><span style={{ fontSize: '20px', fontWeight: 700, color: '#16a34a' }}>{entry.survival_rate != null ? `${entry.survival_rate}%` : '-'}</span></div>
            <div><span style={{ fontSize: '13px', color: '#6b7280' }}>Overall Health</span><br /><span style={{ fontSize: '20px', fontWeight: 700, color: '#1a7431' }}>{entry.overall_health ? entry.overall_health.charAt(0).toUpperCase() + entry.overall_health.slice(1) : '-'}</span></div>
          </div>
        </div>

        {/* Mini chart */}
        <div style={{ background: '#fff', borderRadius: '14px', padding: '24px', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
          <div style={{ fontSize: '16px', fontWeight: 600, marginBottom: '16px', paddingBottom: '12px', borderBottom: '1px solid #f0f0f0' }}>
            Health Distribution
          </div>
          <div style={{ height: '240px' }}>
            <Doughnut data={miniHealthData} options={{ responsive: true, maintainAspectRatio: false, cutout: '55%', plugins: { legend: { position: 'bottom', labels: { padding: 12, usePointStyle: true, font: { size: 11 } } } } }} />
          </div>
          {entry.latitude != null && entry.longitude != null && (
            <div style={{ marginTop: '16px', padding: '10px 14px', background: '#f3f4f6', borderRadius: '10px', fontSize: '12px', color: '#6b7280' }}>
              📍 GPS: {Number(entry.latitude).toFixed(6)}, {Number(entry.longitude).toFixed(6)}
            </div>
          )}
        </div>
      </div>

      {/* Photos */}
      {entry.photos && entry.photos.length > 0 && (
        <div style={{ background: '#fff', borderRadius: '14px', padding: '24px', boxShadow: '0 2px 12px rgba(0,0,0,0.06)', marginBottom: '24px' }}>
          <div style={{ fontSize: '16px', fontWeight: 600, marginBottom: '16px', paddingBottom: '12px', borderBottom: '1px solid #f0f0f0' }}>
            📷 Photos ({entry.photos.length})
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '14px' }}>
            {entry.photos.map((photo, i) => (
              <div key={photo.id || i} style={{ position: 'relative', borderRadius: '12px', overflow: 'hidden', border: '1px solid #e5e7eb' }}>
                <img
                  src={photo.photo_url.startsWith('http') ? photo.photo_url : `${apiBase}${photo.photo_url}`}
                  alt={photo.photo_type}
                  style={{ width: '100%', height: '180px', objectFit: 'cover', display: 'block' }}
                  onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                />
                <div style={{ display: 'none', width: '100%', height: '180px', alignItems: 'center', justifyContent: 'center', background: '#f0fdf4', color: '#16a34a', fontSize: '36px' }}>🌳</div>
                <span style={{ position: 'absolute', top: '8px', left: '8px', background: 'rgba(0,0,0,0.65)', color: '#fff', padding: '3px 10px', borderRadius: '8px', fontSize: '11px', fontWeight: 600, textTransform: 'uppercase' }}>
                  {photo.photo_type}
                </span>
                {photo.latitude && photo.longitude && (
                  <span style={{ position: 'absolute', bottom: '8px', right: '8px', background: 'rgba(0,0,0,0.65)', color: '#fff', padding: '2px 8px', borderRadius: '6px', fontSize: '10px' }}>
                    📍 {Number(photo.latitude).toFixed(4)}, {Number(photo.longitude).toFixed(4)}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Samples */}
      {entry.samples && entry.samples.length > 0 && (
        <div style={{ background: '#fff', borderRadius: '14px', padding: '24px', boxShadow: '0 2px 12px rgba(0,0,0,0.06)', marginBottom: '24px' }}>
          <div style={{ fontSize: '16px', fontWeight: 600, marginBottom: '16px', paddingBottom: '12px', borderBottom: '1px solid #f0f0f0' }}>
            🔬 Sample Measurements ({entry.samples.length})
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #f0f0f0' }}>
                  {['#', 'Tree ID', 'Height (cm)', 'Girth (cm)', 'Health', 'Notes'].map(h => (
                    <th key={h} style={{ padding: '10px 12px', textAlign: 'left', color: '#6b7280', fontWeight: 600, fontSize: '12px' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {entry.samples.map(s => (
                  <tr key={s.id || s.sample_number} style={{ borderBottom: '1px solid #f5f5f5' }}>
                    <td style={{ padding: '10px 12px', fontWeight: 600 }}>{s.sample_number}</td>
                    <td style={{ padding: '10px 12px' }}>{s.tree_identifier || '-'}</td>
                    <td style={{ padding: '10px 12px' }}>{s.height_cm != null ? Number(s.height_cm).toFixed(1) : '-'}</td>
                    <td style={{ padding: '10px 12px' }}>{s.girth_cm != null ? Number(s.girth_cm).toFixed(1) : '-'}</td>
                    <td style={{ padding: '10px 12px' }}>{healthBadge(s.health_status)}</td>
                    <td style={{ padding: '10px 12px', color: '#6b7280' }}>{s.notes || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Notes */}
      {entry.notes && (
        <div style={{ background: '#fff', borderRadius: '14px', padding: '24px', boxShadow: '0 2px 12px rgba(0,0,0,0.06)', marginBottom: '24px' }}>
          <div style={{ fontSize: '16px', fontWeight: 600, marginBottom: '12px' }}>📝 Notes</div>
          <div style={{ fontSize: '14px', color: '#374151', lineHeight: 1.6 }}>{entry.notes}</div>
        </div>
      )}

      {/* Review Notes */}
      {entry.review_notes && (
        <div style={{ background: '#fffbeb', border: '1px solid #fbbf24', borderRadius: '14px', padding: '24px', marginBottom: '24px' }}>
          <div style={{ fontSize: '16px', fontWeight: 600, color: '#b45309', marginBottom: '8px' }}>📋 Review Notes</div>
          <div style={{ fontSize: '14px', color: '#92400e', lineHeight: 1.6 }}>{entry.review_notes}</div>
          {entry.reviewed_at && (
            <div style={{ fontSize: '12px', color: '#b45309', marginTop: '8px', opacity: 0.7 }}>
              Reviewed on {new Date(entry.reviewed_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Blog Categories constant
// ---------------------------------------------------------------------------
const BLOG_CATEGORIES = ['Tree Knowledge', 'Heritage', 'CSR Impact', 'Technology', 'Environment', 'Community', 'Green Offerings'];

const INITIAL_BLOG_FORM = { title: '', summary: '', content: '', category: '', tags: '', read_time_min: 5, featured_image: '' };

// ---------------------------------------------------------------------------
// My Blog Posts (reusable across dashboards)
// ---------------------------------------------------------------------------
function MyBlogPosts({ api }) {
  const [blogs, setBlogs] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editPost, setEditPost] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [form, setForm] = useState(INITIAL_BLOG_FORM);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, per_page: 10 };
      if (statusFilter) params.status = statusFilter;
      const res = await api.getMyBlogs(params);
      setBlogs(res.data.items || []);
      setPages(res.data.pages || 1);
    } catch {
      toast.error('Failed to load blog posts');
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, api]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditPost(null);
    setForm(INITIAL_BLOG_FORM);
    setShowModal(true);
  };

  const openEdit = (post) => {
    setEditPost(post);
    setForm({
      title: post.title || '',
      summary: post.summary || '',
      content: post.content || '',
      category: post.category || '',
      tags: post.tags || '',
      read_time_min: post.read_time_min || 5,
      featured_image: post.featured_image || '',
    });
    setShowModal(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: name === 'read_time_min' ? parseInt(value) || 0 : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.content) {
      toast.warn('Title and content are required');
      return;
    }
    setSubmitting(true);
    try {
      if (editPost) {
        await api.updateBlog(editPost.id, form);
        toast.success('Blog post updated successfully!');
      } else {
        await api.createBlog(form);
        toast.success('Blog post created successfully!');
      }
      setShowModal(false);
      setEditPost(null);
      setForm(INITIAL_BLOG_FORM);
      setPage(1);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save blog post');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this blog post?')) return;
    try {
      await api.deleteBlog(id);
      toast.success('Blog post deleted');
      load();
    } catch {
      toast.error('Failed to delete blog post');
    }
  };

  return (
    <div>
      <div className="page-header">
        <h3>My Blog Posts</h3>
        <button className="btn btn-primary" onClick={openCreate}>+ Create Blog Post</button>
      </div>

      {/* Status filter buttons */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {[
          { value: '', label: 'All' },
          { value: 'pending_review', label: 'Pending Review' },
          { value: 'published', label: 'Published' },
          { value: 'rejected', label: 'Rejected' },
        ].map(s => (
          <button key={s.value} onClick={() => { setStatusFilter(s.value); setPage(1); }}
            className={`btn btn-sm ${statusFilter === s.value ? 'btn-primary' : 'btn-secondary'}`}>
            {s.label}
          </button>
        ))}
      </div>

      {/* Blog editor modal */}
      <Modal open={showModal} title={editPost ? 'Edit Blog Post' : 'Create Blog Post'} onClose={() => { setShowModal(false); setEditPost(null); }}>
        <form onSubmit={handleSubmit} className="form">
          <div className="form-group">
            <label>Title *</label>
            <input type="text" name="title" value={form.title} onChange={handleChange} placeholder="Blog post title" required />
          </div>
          <div className="form-group">
            <label>Summary</label>
            <textarea name="summary" value={form.summary} onChange={handleChange} rows={2} placeholder="Brief summary of your post" />
          </div>
          <div className="form-group">
            <label>Content *</label>
            <textarea name="content" value={form.content} onChange={handleChange} rows={8} placeholder="Write your blog post content..." required />
          </div>
          <div className="form-group">
            <label>Category</label>
            <select name="category" value={form.category} onChange={handleChange}>
              <option value="">Select a category</option>
              {BLOG_CATEGORIES.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Tags (comma-separated)</label>
            <input type="text" name="tags" value={form.tags} onChange={handleChange} placeholder="e.g. trees, sustainability, green" />
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <div className="form-group" style={{ flex: 1 }}>
              <label>Read Time (minutes)</label>
              <input type="number" name="read_time_min" value={form.read_time_min} onChange={handleChange} min={1} max={60} />
            </div>
            <div className="form-group" style={{ flex: 2 }}>
              <label>Featured Image URL</label>
              <input type="text" name="featured_image" value={form.featured_image} onChange={handleChange} placeholder="https://example.com/image.jpg" />
            </div>
          </div>
          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={() => { setShowModal(false); setEditPost(null); }} disabled={submitting}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? 'Saving...' : editPost ? 'Update Post' : 'Create Post'}
            </button>
          </div>
        </form>
      </Modal>

      {loading ? (
        <div className="loading-spinner">Loading...</div>
      ) : blogs.length === 0 ? (
        <div className="empty-state-container">
          <p className="empty-state">No blog posts found.</p>
          <button className="btn btn-primary" onClick={openCreate}>Create Your First Blog Post</button>
        </div>
      ) : (
        <>
          <div className="card">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Category</th>
                  <th>Status</th>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {blogs.map(post => (
                  <tr key={post.id}>
                    <td>
                      <strong>{post.title}</strong>
                      {post.status === 'rejected' && post.rejection_reason && (
                        <div style={{ fontSize: '12px', color: '#b45309', marginTop: '4px' }}>
                          Rejection reason: {post.rejection_reason}
                        </div>
                      )}
                    </td>
                    <td>{post.category || '-'}</td>
                    <td><StatusBadge status={post.status} /></td>
                    <td>{formatDate(post.created_at)}</td>
                    <td style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {post.status !== 'published' && (
                        <button className="btn btn-sm btn-secondary" onClick={() => openEdit(post)}>Edit</button>
                      )}
                      {post.status !== 'published' && (
                        <button className="btn btn-sm" style={{ background: '#ef5350', color: '#fff' }} onClick={() => handleDelete(post.id)}>Delete</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination page={page} pages={pages} onPageChange={setPage} />
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main CSR Dashboard (router shell)
// ---------------------------------------------------------------------------
export default function CSRDashboard() {
  return (
    <DashboardLayout navItems={navItems} title="CSR Dashboard">
      <Routes>
        <Route path="" element={<Overview />} />
        <Route path="targets" element={<TargetsList />} />
        <Route path="targets/:id" element={<TargetDetail />} />
        <Route path="agencies" element={<BrowseAgencies />} />
        <Route path="agencies/:id" element={<AgencyDetail />} />
        <Route path="adopt" element={<CSRBrowseAdoptionSites />} />
        <Route path="my-adoptions" element={<CSRMyAdoptionsList />} />
        <Route path="my-adoptions/:id" element={<CSRAdoptionDetail />} />
        <Route path="monitoring" element={<MonitoringOverview />} />
        <Route path="monitoring/:id" element={<MonitoringEntryDetail />} />
        <Route path="ideas" element={<CSRIdeas />} />
        <Route path="blog" element={<MyBlogPosts api={csrAPI} />} />
        <Route path="legacy" element={<LegacyData />} />
        <Route path="species-report" element={<SpeciesReport />} />
      </Routes>
    </DashboardLayout>
  );
}
